/* eslint-disable no-undef */
import React, {
  ReactElement, useEffect, useRef, useState, useCallback, useMemo,
} from 'react';
import {
  Image, StyleSheet, Text, View, ImageBackground, Animated as RNAnimated, Pressable, ScrollView,
  ActivityIndicator, TextInput,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedProps, useAnimatedStyle, useDerivedValue, useSharedValue, withTiming,
} from 'react-native-reanimated';
import Moment from 'moment';
import Svg, { Ellipse, Line, Polygon } from 'react-native-svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Animatable from 'react-native-animatable';
import { useDispatch, useSelector } from 'react-redux';
import polylabel from 'polylabel';
import Modal from 'react-native-modal';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../constants/Colors';
import PlusIcon from '../components/16pxIcons/PlusIcon';
import { RootTabScreenProps } from '../types';
import MinusIcon from '../components/16pxIcons/MinusIcon';
import ShareIcon from '../components/24pxIcons/ShareIcon';
import AlertModal from '../components/AlertModal';
import getAssets from '../requests/Assets/GetAssets';
import Sensor from '../components/Map/Sensor';
import GetSensorData from '../requests/Sensors/GetSensorData';
import EditIcon from '../components/EditIcon';
import DeleteIcon16 from '../components/16pxIcons/DeleteIcon16';
import PenIcon16 from '../components/16pxIcons/PenIcon16';
import SetFloorsModal from '../components/Map/SetFloorsModal';
import getFloorData from '../requests/Locations/Floors/GetFloorData';
import {
  Area, Floor, AreaType, LocationSubtypeTitles, Space,
} from '../components/Map/SetFloorsModal/FloorItem';
import AreaView from '../components/Map/AreaView';
import LeftArrow from '../components/16pxIcons/LeftArrow';
import RightArrow24 from '../components/24pxIcons/RightArrow24';
import addArea from '../requests/Locations/Areas/AddArea';
import getAreas from '../requests/Locations/Areas/GetAreas';
import Asset from '../components/Map/Asset';
import RightArrow from '../components/16pxIcons/RightArrow';
import EditIcon24 from '../components/24pxIcons/EditIcon24';
import addSpace from '../requests/Locations/Spaces/AddSpace';
import getSpaces from '../requests/Locations/Spaces/GetSpaces';
import Style from '../constants/Style';
import editArea from '../requests/Locations/Areas/EditArea';
import editSpace from '../requests/Locations/Spaces/EditSpace';
import QuestionIcon from '../components/24pxIcons/QuestionIcon';
import MapLegend from '../components/Map/MapLegend';
import AssetRow from '../components/Map/AssetRow';
import Ticket from '../components/Map/Ticket';
import getTickets from '../requests/Tickets/GetTickets';
import DateComponent from '../components/DateComponent';
import TicketsFilter from './Tickets/components/TicketsFilter';
import FilterIcon16 from '../components/16pxIcons/FilterIcon';
import AddIcon60 from '../components/60pxIcons/AddIcon60';
import TicketRow from '../components/Map/TicketRow';
import PinIcon from '../components/Map/PinIcon';
import { DateRange, DateSelectionRange, updateDateRange } from '../redux/dateRangeSlice';
import { logEvent } from '../components/LogEvent';
import SearchIcon from '../components/SearchIcon';
import getUsers from '../requests/Users/GetUsers';
import { resetAllTicketFilters, TicketsFilterState } from '../redux/ticketsFilterSlice';
import { PressableState } from '../components/PressableStyled';
import AddAreaModal from '../components/Map/AddAreaModal';
import OccupancyWidgets from '../components/Map/OccupancyWidgets';
import EyeIcon from '../components/12pxIcons/EyeIcon';
import ScreenEmptyState from '../components/ScreenEmptyState';
import {
  TemplateType, MeterLabel, Metric, SensorLabel, UserGroup,
  AssetInstance, Template,
} from '../constants/Types';
import MapTabButton from '../components/Map/MapTabButton';
import SensorTabButton from '../components/Map/SensorTabButton';
import {
  resetSensorFilter, SensorsFilterState, updateSensorFilter,
} from '../redux/sensorsFilterSlice';
import getUserGroups from '../requests/Users/GetUserGroups';
import {
  ADD_AREA_SUCCESS, ADD_SPACE_SUCCESS, EDIT_AREA_SUCCESS, EDIT_SPACE_SUCCESS,
  FEATURE_COMING_SOON, POINT_WITHIN_AREA_ERROR, PROVIDE_CAPACITY,
} from '../constants/AlertMessages';
import ShieldCheckIcon from '../components/ShieldCheckIcon';
import MotionDetectedIcon from '../components/MotionDetectedIcon';
import GetGuardTourData from '../requests/Sensors/Occupancy/GetGuardTourData';
import { updateLocations } from '../redux/locationStructureSlice';
import ScrollViewStyled from '../components/ScrollViewStyled';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import GetAverageUtilization from '../requests/Sensors/Occupancy/GetAverageUtilization';
import Switch from '../components/Switch';
import HelpCircleIcon from '../components/HelpCircleIcon';
import ExpandIcon from '../components/24pxIcons/ExpandIcon';
import DrawPoints from '../components/Map/DrawPoints';
import DrawRectGrab from '../components/Map/DrawRectGrab';
import DrawTools from '../components/Map/DrawTools';
import SubLocationToggleSwitch from '../components/Map/SubLocationToggleSwitch';
import SideBar from '../components/Map/SideBar';
import DrawEllipse from '../components/Map/DrawEllipse';
import hydrateSensorMetricsFromPayload from '../components/Map/sensorPayloadMetrics';
import getTemplateGroups from '../requests/Assets/GetTemplateGroups';
import { updateMapScreenSelection } from '../redux/mapScreenSlice';
import { BuildingSettingsPriority } from './LocationManagement/components/BuildingSettingsModal/data';
import { Building } from './LocationManagement/components/AddBuildingModal';

type SensorPinWrapperProps = {
  sensorId: string;
  locationId: string | null;
  menuOpenId: string;
  movingAssetId: string | null;
  focusedAssetId: any;
  focusedLocationId: any;
  children: ReactElement;
};

function SensorPinWrapper({
  sensorId,
  locationId,
  menuOpenId,
  movingAssetId,
  focusedAssetId,
  focusedLocationId,
  children,
}: SensorPinWrapperProps) {
  const hovered = useSharedValue(false);

  const animatedStyle = useAnimatedStyle(() => ({
    zIndex: (hovered.value
      || focusedAssetId.value === sensorId
      || menuOpenId === sensorId) ? 100 : 1,
  }), [sensorId, menuOpenId, focusedAssetId]);

  return (
    <Animated.View style={[{ position: 'absolute' }, animatedStyle]}>
      <Pressable
        onHoverIn={() => {
          hovered.value = true;
          if (!movingAssetId) {
            focusedAssetId.value = sensorId;
            focusedLocationId.value = locationId;
          }
        }}
        onHoverOut={() => {
          hovered.value = false;
          if (!movingAssetId) {
            focusedAssetId.value = null;
            focusedLocationId.value = null;
          }
        }}
        style={{ cursor: 'auto' }}
      >
        {children}
      </Pressable>
    </Animated.View>
  );
}

type AssetPinWrapperProps = {
  assetId: string;
  locationId: string | null;
  menuOpenId: string;
  movingAssetId: string | null;
  focusedAssetId: any;
  focusedLocationId: any;
  children: ReactElement;
};

const SHOW_MINI_LEGEND_STORAGE_KEY = 'mapScreen.showMiniLegend';

function AssetPinWrapper({
  assetId,
  locationId,
  menuOpenId,
  movingAssetId,
  focusedAssetId,
  focusedLocationId,
  children,
}: AssetPinWrapperProps) {
  const hovered = useSharedValue(false);

  const animatedStyle = useAnimatedStyle(() => ({
    zIndex: (hovered.value
      || movingAssetId === assetId
      || focusedAssetId.value === assetId
      || menuOpenId === assetId) ? 10 : 0,
  }), [assetId, menuOpenId, movingAssetId, focusedAssetId]);

  return (
    <Animated.View style={[{ position: 'absolute' }, animatedStyle]}>
      <Pressable
        onHoverIn={() => {
          hovered.value = true;
          if (!movingAssetId) {
            focusedAssetId.value = assetId;
            focusedLocationId.value = locationId;
          }
        }}
        onHoverOut={() => {
          hovered.value = false;
          if (!movingAssetId) {
            focusedAssetId.value = null;
            focusedLocationId.value = null;
          }
        }}
      >
        {children}
      </Pressable>
    </Animated.View>
  );
}

export default function MapScreen({ navigation, route }: RootTabScreenProps<'Map'>) {
  const happyEnabled = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const filterState: TicketsFilterState = useSelector((state:
    { ticketsFilter: TicketsFilterState }) => state.ticketsFilter);
  const [clientName, setClientName] = useState<string>(null);
  const [floorPlanModalVisible, setFloorPlanModalVisible] = useState<boolean>(false);

  const savedScale = useSharedValue<number>(1);
  const negativeScale = useSharedValue<number>(1);

  const viewWidth = useSharedValue(0);
  const viewHeight = useSharedValue(0);

  const translateX = useSharedValue<number>(0);
  const translateY = useSharedValue<number>(0);

  const [xStart, setXStart] = useState<number>(0);
  const [xEnd, setXEnd] = useState<number>(0);
  const [yStart, setYStart] = useState<number>(0);
  const [yEnd, setYEnd] = useState<number>(0);

  const [mapViewWidth, setMapViewWidth] = useState<number>(0);
  const [mapViewHeight, setMapViewHeight] = useState<number>(0);
  const floorAssetsRequestIdRef = useRef<number>(0);
  const currentFloorIdRef = useRef<string | null>(null);

  const [imageWidth, setImageWidth] = useState<number>(1275);
  const [imageHeight, setImageHeight] = useState<number>(1169);

  const [zoomOutDisabled, setZoomOutDisabled] = useState<boolean>(false);
  const [zoomInDisabled, setZoomInDisabled] = useState<boolean>(false);

  const [sensors, setSensors] = useState([]);
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [movingAssetId, setMovingAssetId] = useState<string | null>(null);
  const [movingLocationId, setMovingLocationId] = useState<string | null>(null);
  const [assetClusters, setAssetClusters] = useState([]);
  const [sensorClusters, setSensorClusters] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertCancelTitle, setAlertCancelTitle] = useState<string | null>(null);
  const [alertTitle, setAlertTitle] = useState<string | null>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string | null>(null);
  const [movingItem, setMovingItem] = useState(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [drawMode, setDrawMode] = useState<boolean>(false);

  const [drawPoints, setDrawPoints] = useState([]);
  const [redoPoints, setRedoPoints] = useState([]);

  const [areaComplete, setAreaComplete] = useState<boolean>(false);
  const [areas, setAreas] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [shapeName, setShapeName] = useState<string>('');
  const [locationSubtype, setLocationSubtype] = useState<AreaType>(null);
  const [locationCapacity, setLocationCapacity] = useState<number | null>(null);
  const [locationExternalId, setLocationExternalId] = useState<string>(null);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object | null | undefined = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const selectedBuilding: Building | undefined | null = buildings
    ? buildings[selectedBuildingId] : null;
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const smartGuardRoute: boolean = useSelector((state:
    { mapScreen: { smartGuardTour: boolean } }) => state.mapScreen.smartGuardTour);
  const [floorData, setFloorData] = useState<Floor[]>([]);
  const [highPriority, setHighPriority] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const drawerInset = useSharedValue(0);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [prepareAreaSelection, setPrepareAreaSelection] = useState<boolean>(false);
  const [prepareSpaceSelection, setPrepareSpaceSelection] = useState<boolean>(false);
  const [showAreaList, setShowAreaList] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const focusedLocationId = useSharedValue<string | null>(null);
  const focusedAssetId = useSharedValue<string | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<Floor>(null);
  const [editingShape, setEditingShape] = useState<boolean>(false);
  const [editLocationId, setEditLocationId] = useState<string | null>(null);
  const [editLocationType, setEditLocationType] = useState<'area' | 'space' | null>(null);
  const [beingEdited, setBeingEdited] = useState(null);
  const [legendShown, setLegendShown] = useState<boolean>(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingFloor, setLoadingFloor] = useState<boolean>(true);
  const [doneFilter, setDoneFilter] = useState<boolean>(false);
  const [inProgressFilter, setInProgressFilter] = useState<boolean>(false);
  const [notStartedFilter, setNotStartedFilter] = useState<boolean>(false);
  const [otherPriorityFilter, setOtherPriorityFilter] = useState<boolean>(false);
  const [lowPriorityFilter, setLowPriorityFilter] = useState<boolean>(false);
  const [mediumPriorityFilter, setMediumPriorityFilter] = useState<boolean>(false);
  const [highPriorityFilter, setHighPriorityFilter] = useState<boolean>(false);
  const [criticalPriorityFilter, setCriticalPriorityFilter] = useState<boolean>(false);
  const [slaOverdueFilter, setSlaOverdueFilter] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<string>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [selectedAssigneeName, setSelectedAssigneeName] = useState<string>('');
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedIssueId, setSelectedIssueId] = useState<string>('');
  const [dateOpen, setDateOpen] = useState<boolean>(false);
  const scale = useSharedValue(1);
  const [scaleLabel, setScaleLabel] = useState(100);
  const [areaUpdated, setAreaUpdated] = useState<boolean>(false);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(null);
  const [floorUpdate, setFloorUpdate] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [locationSearchValue, setLocationSearchValue] = useState<string>('');
  const [saveFilterToggled, setSaveFilterToggled] = useState<boolean>(false);
  const [savedFiltersLoaded, setSavedFiltersLoaded] = useState<boolean>(false);
  const [assetContextIndex, setAssetContextIndex] = useState<string>('');
  const [smartGuardListView, setSmartGuardListView] = useState<boolean>(true);
  const [pinMenuOpen, setPinMenuOpen] = useState<string>('');
  const [assetMenuOpen, setAssetMenuOpen] = useState<string>('');
  const [sensorMenuOpen, setSensorMenuOpen] = useState<string>('');
  // eslint-disable-next-line no-unused-vars
  const [guardLastUpdated, setGuardLastUpdated] = useState(null);
  const [subLocationsEnabled, setSubLocationsEnabled] = useState<boolean>(false);
  const [clusteringDisabled, setClusteringDisabled] = useState<boolean>(false);
  const [showNewSidebar, setShowNewSidebar] = useState<boolean>(true);
  const [toolDialogVisible, setToolDialogVisible] = useState<boolean>(false);
  const [selectedTool, setSelectedTool] = useState<string>('draw');
  const [showMiniLegend, setShowMiniLegend] = useState<boolean>(true);
  const [showMiniLegendLoaded, setShowMiniLegendLoaded] = useState<boolean>(false);
  const subLocationsProgress = useSharedValue(0);

  const [selectedTab, setSelectedTab] = useState<'tickets' | 'assets' | 'occupancy' | 'sensors'>('tickets');

  const AnimatedPolygon = Animated.createAnimatedComponent(Polygon);
  const AnimatedEllipse = Animated.createAnimatedComponent(Ellipse);

  const drawModeRef = useRef(drawMode);
  const movingItemRef = useRef(movingItem);
  const slaNames: string[] = useSelector(
    (state: { selectedBuilding: { SlaHours: BuildingSettingsPriority[] } }) => state
      .selectedBuilding.SlaHours?.map((item: BuildingSettingsPriority) => item.Name),
  );

  const [range, setRange] = useState<DateRange>({
    start: Moment().subtract(6, 'days').startOf('day')
      .toDate()
      .toISOString(),
    end: new Date().toISOString(),
    selectedRange: 1,
  });
  const lastPoll = useSelector((state: { dateRange: DateRange }) => state.dateRange.lastPoll);
  const buildingScreenRange: DateRange = useSelector((state:
    { dateRange: DateRange }) => state.dateRange);
  const liveDateRange: DateRange = useSelector((state:
    { liveDateRange: DateRange }) => state.liveDateRange);

  const occupancySelected: boolean = selectedTab === 'occupancy';

  const hasPcsAssets = (area: any): boolean => Number(area?.assetCountByLabel?.PCS ?? 0) > 0;

  const displayAreas = (occupancySelected && !smartGuardRoute) ? areas.filter(hasPcsAssets) : areas;

  const occupancyShown: boolean = occupancySelected || smartGuardRoute;

  const sensorsShown: boolean = selectedTab === 'sensors';

  const ticketsSelected: boolean = selectedTab === 'tickets';

  const assetsSelected: boolean = selectedTab === 'assets';

  const areaLocationIds = useMemo(
    () => new Set((areas ?? [])
      .map((area: any) => String(area?.LocationId ?? ''))
      .filter((id) => id.length > 0)),
    [areas],
  );

  const spaceLocationIds = useMemo(
    () => new Set((spaces ?? [])
      .map((space: any) => String(space?.LocationId ?? ''))
      .filter((id) => id.length > 0)),
    [spaces],
  );

  const shouldHideSubLocationItems = !smartGuardRoute
    && !subLocationsEnabled
    && !selectedArea
    && !selectedSpace;

  const subLocationPolygons = useMemo(() => {
    const areaPoints = (areas ?? [])
      .map((area: any) => area?.points)
      .filter((points: any) => Array.isArray(points) && points.length > 2);
    const spacePoints = (spaces ?? [])
      .map((space: any) => space?.points)
      .filter((points: any) => Array.isArray(points) && points.length > 2);
    return [...areaPoints, ...spacePoints];
  }, [areas, spaces]);

  const isPointInPolygon = useCallback((x: number, y: number, points: any[]): boolean => {
    if (!Array.isArray(points) || points.length < 3) return false;
    let inside = false;
    let i = 0;
    let j = points.length - 1;
    while (i < points.length) {
      const xi = points[i]?.x;
      const yi = points[i]?.y;
      const xj = points[j]?.x;
      const yj = points[j]?.y;
      const intersects = ((yi > y) !== (yj > y))
        && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-9) + xi);
      if (intersects) inside = !inside;
      j = i;
      i += 1;
    }
    return inside;
  }, []);

  const getItemGeoPoint = useCallback((item: any): { x: number, y: number } | null => {
    const directX = Number(item?.GeoX);
    const directY = Number(item?.GeoY);
    if (Number.isFinite(directX) && Number.isFinite(directY)) {
      return { x: directX, y: directY };
    }
    const fields = item?.customFields ?? [];
    const geoXField = fields.find((field: any) => field?.name === 'GeoX');
    const geoYField = fields.find((field: any) => field?.name === 'GeoY');
    const fieldX = Number(geoXField?.value);
    const fieldY = Number(geoYField?.value);
    if (Number.isFinite(fieldX) && Number.isFinite(fieldY)) {
      return { x: fieldX, y: fieldY };
    }
    return null;
  }, []);

  const isItemInSubLocation = useCallback((item: any): boolean => {
    if (!item) return false;
    const areaId = item.areaId ?? item.AreaId;
    const spaceId = item.spaceId ?? item.SpaceId;
    if (areaId || spaceId) return true;
    const locationId = String(item.locationId ?? item.LocationId ?? '');
    const selectedFloorId = String(selectedFloor?.LocationId ?? '');
    if (selectedFloorId && locationId && locationId !== selectedFloorId) return true;
    if (areaLocationIds.has(locationId) || spaceLocationIds.has(locationId)) return true;

    const point = getItemGeoPoint(item);
    if (!point) return false;

    return subLocationPolygons.some((polygonPoints: any[]) => (
      isPointInPolygon(point.x, point.y, polygonPoints)
    ));
  }, [
    areaLocationIds,
    getItemGeoPoint,
    isPointInPolygon,
    selectedFloor?.LocationId,
    spaceLocationIds,
    subLocationPolygons,
  ]);

  const visibleAssets: AssetInstance[] = useMemo(() => {
    if (!shouldHideSubLocationItems) return assets;
    return assets.filter((asset) => !isItemInSubLocation(asset));
  }, [assets, shouldHideSubLocationItems, isItemInSubLocation]);

  const visibleSensors = useMemo(() => {
    if (!shouldHideSubLocationItems) return sensors;
    return sensors.filter((sensor) => !isItemInSubLocation(sensor));
  }, [sensors, shouldHideSubLocationItems, isItemInSubLocation]);

  const sensorsFilter: SensorsFilterState = useSelector((state:
    { sensorsFilter: SensorsFilterState }) => state.sensorsFilter);
  const selectedFilterCount: number = Object.values(sensorsFilter)
    .filter((value: boolean) => value).length;
  const isAdmin: boolean = useSelector((state:
    { user: { isAdmin: boolean } }) => state.user.isAdmin);
  // eslint-disable-next-line no-unused-vars
  const isDev: boolean = useSelector((state:
    { devState: { isDev: boolean } }) => state.devState.isDev);

  const dispatch = useDispatch();

  const onConfirm = useCallback((start, end, selectedRange) => {
    // setAssets([]);
    // setLoading(true);
    setRange({ start, end, selectedRange });
    setBuildingScreenRange({ start, end, selectedRange });
  }, [setRange]);

  const setBuildingScreenRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const onOccupancyDateConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => {
      setBuildingScreenRange({ start, end, selectedRange });
    },
    [setBuildingScreenRange],
  );

  const anyFilterApplied = () => otherPriorityFilter
    || lowPriorityFilter
    || mediumPriorityFilter
    || highPriorityFilter
    || criticalPriorityFilter
    || doneFilter
    || notStartedFilter
    || inProgressFilter;

  const labelTypeMap: { [key: string]: string } = {
    [MeterLabel.WM]: 'water',
    [MeterLabel.EM]: 'electric',
    [MeterLabel.HM]: 'heat',
    [SensorLabel.LS]: 'pir',
    [SensorLabel.PCS]: 'pcs',
    [SensorLabel.ENT]: 'ent',
  };

  const selectFloor = (floors: Floor[]) => {
    setSelectedFloor(null);
    const selectedFloorId: string = selectedFloor?.LocationId;
    const navigationFloorId: string = (route.params as any)?.floorId;
    const editModeRequested: boolean = (route.params as any)?.edit_mode;
    if ((route.params as any)?.assetsView) setSelectedTab('assets');
    if ((route.params as any)?.sensorsView) setSelectedTab('sensors');
    if ((route.params as any)?.ticketsView) setSelectedTab('tickets');
    else if ((route.params as any)?.assetId && !(route.params as any)?.sensorsView) {
      setSelectedTab('assets');
    }
    let floor: Floor = floors.filter((f: Floor) => f.LocationId
      === (navigationFloorId ?? selectedFloorId))[0] ?? floors[0];
    if ((route.params as any)?.floor) {
      const name = (route.params as any)?.floor;
      floor = floors.filter((f: Floor) => f.Name === (name))[0] ?? floors[0];
      if ((route.params as any)?.placeAsset) {
        dispatch(resetSensorFilter());
        if (assets?.length > 0) {
          const asset: any = assets.find((a: AssetInstance) => a.id === (route.params as any)
            ?.assetId);
          if (asset && !asset.hasXY) {
            setEditMode(true);
            setMovingItem(true);
            setDrawerOpen(false);
            setDrawMode(false);
            asset.hasXY = true;
            if (selectedArea) {
              asset.GeoY = selectedArea.pinY;
              asset.GeoX = selectedArea.pinX;
              asset.initialPlacement = true;
            } else {
              asset.GeoY = 120;
              asset.GeoX = 20;
            }
            setMovingAssetId(asset.id);
            asset.locationFloor = selectedFloor?.Name;
          }
        }
      } else setEditMode(false);
    }
    setSelectedFloor(floor);
    setLoading(true);
    if (navigationFloorId || (route.params as any)?.floor) {
      setSelectedTab('assets');
      if (editModeRequested) setEditMode(true);
      const currentParams: {
        floorId?: string; floor?: string; areaId?: string; area?: string; edit_mode?: boolean
      } = route.params || {};
      if (currentParams.floorId || currentParams.floor || currentParams.edit_mode !== false) {
        navigation.setParams({ floorId: undefined, floor: undefined, edit_mode: false } as any);
      }
      if ((route.params as any)?.areaId || (route.params as any)?.area) {
        setPrepareAreaSelection(true);
      } else setSelectedArea(null);
    }
  };

  const loadFloors = () => {
    if (!selectedBuildingId) return;
    setLoadingFloor(true);
    getFloorData(selectedBuildingId).then((res) => {
      const locations = res?.locations?.sort((prev, next) => prev.Index - next.Index)
        ?.map((location) => ({ ...{ ...location }, planFilename: location.Svg })) ?? [];
      setFloorData(locations);
      if ((route.params as any)?.route !== 'smartguardtour') selectFloor(locations);
      setLoadingFloor(false);
    });
  };

  const smartGuardTimestamp = (timestamp: string) => Moment(timestamp).calendar();

  useEffect(() => {
    if (selectedBuildingId) loadFloors();
  }, [selectedBuildingId, floorUpdate]);

  useEffect(() => {
    if ((route.params as any)?.route !== 'smartguardtour') selectFloor(floorData);

    if ((route.params as any)?.assetId && ((route.params as any)?.assetsView
      || (route.params as any)?.sensorsView)) {
      setTimeout(() => {
        focusedAssetId.value = (route.params as any)?.assetId;
      }, 1000);
      setSelectedTab((route.params as any)?.sensorsView ? 'sensors' : 'assets');
      setSelectedAssetId((route.params as any)?.assetId);
    }

    if ((route.params as any)?.assetId && (route.params as any)?.ticketsView) {
      setTimeout(() => {
        focusedAssetId.value = (route.params as any)?.assetId;
      }, 1000);
    }

    if ((route.params as any)?.ticketDate) {
      const ticketDate: string = (route.params as any)?.ticketDate;
      setRange({
        start: Moment(ticketDate).startOf('day').toDate().toISOString(),
        end: Moment(ticketDate).endOf('day').toDate().toISOString(),
        selectedRange: 0,
      });
    }

    if ((route.params as any)?.route === 'smartguardtour') {
      setSelectedTab('occupancy');
      setShowAreaList(true);
      setSmartGuardListView(true);
      setSelectedFloor(null);
      setSelectedArea(null);
      setDrawerOpen(false);
      dispatch(updateMapScreenSelection(true));
      dispatch(resetSensorFilter());
      dispatch(updateSensorFilter({ key: 'LS', value: true }));
    } else dispatch(updateMapScreenSelection(false));
  }, [route]);

  const selectArea = () => {
    const navigationAreaId: string = (route.params as any)?.areaId;
    const editModeRequested: boolean = (route.params as any)?.edit_mode;
    const navigationAreaName: string = (route.params as any)?.area;
    let area: Area = areas.find((a: Area) => a?.LocationId
      === (navigationAreaId ?? selectedArea?.LocationId));
    if (!navigationAreaId && navigationAreaName) {
      area = areas.find((a) => a.name === navigationAreaName) ?? area;
    }
    if (area) {
      setSelectedArea(area);
      if (editModeRequested) setEditMode(true);
      const currentParams: {
        floorId?: string; areaId?: string;
        area?: string; edit_mode?: boolean
      } = route.params || {};
      if (
        currentParams.floorId !== undefined
        || currentParams.areaId !== undefined
        || currentParams.area !== undefined
        || currentParams.edit_mode !== false
      ) {
        navigation.setParams({
          floorId: undefined, areaId: undefined, area: undefined, edit_mode: false,
        } as any);
        if ((route.params as any)?.spaceId || (route.params as any)?.space) {
          setPrepareSpaceSelection(true);
        } else setSelectedSpace(null);
      }
    } else setSelectedArea(null);
  };

  const selectSpace = () => {
    const navigationSpaceId: string = (route.params as any)?.spaceId;
    const editModeRequested: boolean = (route.params as any)?.edit_mode;
    let space: Space = spaces.filter((s: Space) => !!s && s.LocationId
      && s.LocationId === (navigationSpaceId ?? selectedSpace?.LocationId))[0];
    const name = (route.params as any)?.space;
    if (name) space = spaces.filter((a) => a.name === name)[0] ?? null;
    if (space) {
      setSelectedSpace(space);
      if (navigationSpaceId) {
        setSelectedTab('assets');
        if (editModeRequested) setEditMode(true);
        navigation.setParams({
          floorId: undefined,
          areaId: undefined,
          spaceId: undefined,
          space: undefined,
          edit_mode: false,
        } as any);
      }
    } else setSelectedSpace(null);
  };

  useEffect(() => {
    if (!prepareAreaSelection || !(areas?.length > 0)
      || areas[0]?.ParentId !== selectedFloor?.LocationId) return;
    selectArea();
    setPrepareAreaSelection(false);
  }, [prepareAreaSelection, areas, selectedFloor]);

  useEffect(() => {
    if (!prepareSpaceSelection || !(spaces?.length > 0)
      || spaces[0].ParentId !== selectedArea.LocationId) return;
    selectSpace();
    setPrepareSpaceSelection(false);
  }, [prepareSpaceSelection, spaces]);

  useEffect(() => {
    subLocationsProgress.value = withTiming(subLocationsEnabled ? 1 : 0, { duration: 150 });
  }, [subLocationsEnabled]);

  const showAlert = (
    title: string,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
    message: string | null = null,
    cancelTitle: string = null,
    cancelAction: () => void = null,
  ) => {
    setAlertError(error);
    setAlertTitle(title);
    setAlertActionTitle(actionTitle);
    setAlertAction(() => action);
    setAlertMessage(message);
    setAlertCancelTitle(cancelTitle);
    setAlertCancelAction(() => cancelAction);
    setAlertVisible(true);
  };

  const [sensorRange] = useState<{
    start: Date | undefined;
    end: Date | undefined;
  }>({ start: Moment().subtract(3, 'hours').toDate(), end: Moment().toDate() });

  const blur: () => void = () => {
    setEditMode(false);
    setMovingItem(false);
    navigation.setParams({ edit_mode: false } as any);
    setFilterOpen(false);
  };

  function euclideanDistance(p1: any, p2: any): number {
    const x1 = p1.GeoX;
    const y1 = p1.GeoY;
    const x2 = p2.GeoX;
    const y2 = p2.GeoY;
    return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
  }

  function clusterCenter(cluster: any[]): [number, number] {
    const numPoints = cluster.length;
    const sum = cluster.reduce(
      (acc, point) => {
        acc[0] += point.GeoX;
        acc[1] += point.GeoY;
        return acc;
      },
      [0, 0],
    );
    return [sum[0] / numPoints, sum[1] / numPoints];
  }

  function hierarchicalClustering(points: any[], threshold: number): number[][][] {
    let clusters = [];
    clusters = points.filter((asset) => asset.hasXY).map((point) => [point]);

    if (threshold === 0 || movingItem
      || selectedAssetId || smartGuardRoute || clusteringDisabled) return clusters;

    function closestPair(): [number, number] {
      let minDistance = Infinity;
      let pair: [number, number] = [0, 0];

      for (let i = 0; i < clusters.length - 1; i += 1) {
        for (let j = i + 1; j < clusters.length; j += 1) {
          const distance = euclideanDistance(clusters[i][0], clusters[j][0]);
          if (distance < minDistance) {
            minDistance = distance;
            pair = [i, j];
          }
        }
      }
      return pair;
    }

    while (clusters.length > 1) {
      const [i, j] = closestPair();

      if (euclideanDistance(clusters[i][0], clusters[j][0]) > threshold) {
        break;
      }
      clusters[i] = clusters[i].concat(clusters[j]);
      clusters.splice(j, 1);
    }

    return clusters;
  }

  function polygonToPoints(polygonString: string) {
    if (!polygonString) return [];

    const pointsArray = [];
    const coordPairs = polygonString.split(',');

    coordPairs.forEach((pair, index) => {
      const [x, y] = pair.trim().split(' ').map((coord) => parseFloat(coord));
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(x) && !isNaN(y)) {
        pointsArray.push({
          x,
          y,
          number: index,
        });
      }
    });
    return pointsArray;
  }

  // eslint-disable-next-line no-unused-vars
  const centerViewOnCoordinate = (targetCoords: { x: number, y: number }, moving = false) => {
    setClusteringDisabled(true);
    const newScale = scale.value + (moving ? 0 : 0.25);
    if (!moving) zoomIn();
    const offsetX = viewWidth.value / 2 - targetCoords.x * newScale;
    const offsetY = viewHeight.value / 2 - targetCoords.y * newScale;

    translateX.value = withTiming(offsetX);
    translateY.value = withTiming(offsetY);
  };

  useEffect(() => {
    const distanceThreshold = savedScale.value === 2.5 ? 0 : 50 / savedScale.value;

    const assetsCopy = Object.assign([], visibleAssets);
    const clusters = hierarchicalClustering(assetsCopy, distanceThreshold);

    setAssetClusters(clusters);

    // console.log('Clusters:', clusters);
  }, [visibleAssets, savedScale.value, movingItem]);

  useEffect(() => {
    const distanceThreshold = savedScale.value === 2.5 ? 0 : 50 / savedScale.value;

    let sensorsCopy = Object.assign([], visibleSensors);
    if (selectedFilterCount) {
      sensorsCopy = [...sensorsCopy].filter((sensor) => {
        let enabled = false;
        (sensor.labels ?? []).forEach((label) => {
          if (enabled) return;
          enabled = sensorsFilter[label];
        });
        return enabled;
      });
    }
    const clusters = hierarchicalClustering(sensorsCopy, distanceThreshold);

    setSensorClusters(clusters);

    // console.log('Clusters:', clusters);
  }, [visibleSensors, savedScale.value, movingItem, selectedFilterCount]);

  useEffect(() => {
    getUsers().then((res) => {
      if (res && !res.isError && res.items?.length > 0) {
        setUsers(res.items);
      }
    });
    AsyncStorage.getItem('clientName').then(setClientName);
    AsyncStorage.getItem(SHOW_MINI_LEGEND_STORAGE_KEY)
      .then((storedValue) => {
        if (storedValue === null) return;
        setShowMiniLegend(storedValue === 'true');
      })
      .finally(() => setShowMiniLegendLoaded(true));
    const unsubscribe = navigation.addListener('blur', blur);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!showMiniLegendLoaded) return;
    AsyncStorage.setItem(SHOW_MINI_LEGEND_STORAGE_KEY, String(showMiniLegend));
  }, [showMiniLegend, showMiniLegendLoaded]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      setLoading(true);
      loadSavedFilters();
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!movingItem) setMovingAssetId(null);
  }, [movingItem]);

  useEffect(() => {
    Image.getSize(
      selectedFloor?.Svg,
      (width, height) => {
        setImageWidth(width);
        setImageHeight(height);
      },
      () => { },
    );
  }, [selectedFloor]);

  useEffect(() => {
    if (!(saveFilterToggled && savedFiltersLoaded)) return;
    setLowPriorityFilter(filterState.lowPriority);
    setMediumPriorityFilter(filterState.mediumPriority);
    setHighPriorityFilter(filterState.highPriority);
    setCriticalPriorityFilter(filterState.criticalPriority);
    setOtherPriorityFilter(filterState.otherPriority);
    setDoneFilter(filterState.doneFilter);
    setInProgressFilter(filterState.inProgressFilter);
    setNotStartedFilter(filterState.notStartedFilter);
    setSelectedAssetId(filterState.selectedAssetId);
    setSelectedTemplateId(filterState.selectedTemplateId);
    setSelectedIssueId(filterState.selectedIssueId);
    setSelectedLocation(filterState.selectedLocation);
  }, [saveFilterToggled, savedFiltersLoaded]);

  useEffect(() => {
    if (!savedFiltersLoaded) return;
    getTickets({
      buildingId: selectedBuildingId,
      other: otherPriorityFilter,
      low: lowPriorityFilter,
      moderate: mediumPriorityFilter,
      high: highPriorityFilter,
      critical: criticalPriorityFilter,
      done: doneFilter,
      inprogress: inProgressFilter,
      reported: notStartedFilter,
      assignee: selectedAssignee,
      assetId: selectedAssetId,
      usergroupIds: [selectedUserGroupId],
      startTime: range.start,
      endTime: range.end,
      locationId: selectedLocation ?? selectedFloor?.LocationId,
      templateId: selectedTemplateId,
      issueId: selectedIssueId,
    }).then((res) => {
      setTickets(res?.tickets ?? []);
    });
  }, [selectedBuildingId, otherPriorityFilter, lowPriorityFilter, mediumPriorityFilter,
    highPriorityFilter, criticalPriorityFilter, doneFilter, inProgressFilter, notStartedFilter,
    selectedAssetId, range.start, range.end, selectedLocation, savedFiltersLoaded, selectedAssignee,
    selectedUserGroupId, selectedFloor, selectedArea, selectedSpace, selectedTemplateId,
  ]);

  const fetchGuardTourData = () => {
    if (showMiniLegend) setShowMiniLegend(false);
    floorData.forEach((floor) => {
      GetGuardTourData({ externalId: floor.ExternalId }).then((res) => {
        if (!res.pirData) return;
        let latestPirTimestamp: Date | null = null;
        floor.motionDetected = false;

        Object.keys(res.pirData).forEach((point) => {
          const pointTimestamp: string | undefined = res.pirData[point].timestamp;
          if (pointTimestamp) {
            const timestampDate = new Date(pointTimestamp);
            if (!latestPirTimestamp || timestampDate > latestPirTimestamp) {
              latestPirTimestamp = timestampDate;
            }
          }

          if (res.pirData[point].pointValue === 'Active') {
            floor.motionDetected = true;
            sensors.forEach((sensor) => {
              if (sensor.id === point) {
                sensor.lastDetected = smartGuardTimestamp(res.pirData[point].timestamp);
                sensor.motionDetected = true;
              }
            });
          }
        });

        if (latestPirTimestamp) {
          floor.guardLastChecked = smartGuardTimestamp(latestPirTimestamp.toISOString());
        }
        setGuardLastUpdated(Moment());
      });
    });
  };

  const fetchSelectedFloorGuardTourData = async () => {
    let guardTourData = null;
    if (selectedFloor?.ExternalId) {
      guardTourData = await GetGuardTourData({ externalId: selectedFloor.ExternalId });
    }
    await sensors.map(async (sensor) => {
      if (guardTourData?.pirData) {
        Object.keys(guardTourData.pirData).forEach((point) => {
          if (guardTourData.pirData[point].pointValue === 'Active') {
            selectedFloor.motionDetected = true;
            if (sensor.id === point) {
              sensor.lastDetected = smartGuardTimestamp(guardTourData.pirData[point].timestamp);
              sensor.motionDetected = true;
            }
            if (guardTourData.pirData[point].timestamp) {
              selectedFloor.guardLastChecked = smartGuardTimestamp(
                guardTourData.pirData[point].timestamp,
              );
            }
          }
        });
      }
      const foundLabel = (sensor.labels ?? []).find((label) => labelTypeMap[label]);
      if (foundLabel) {
        sensor.type = labelTypeMap[foundLabel];
      }
    });
    setGuardLastUpdated(Moment());
  };

  useEffect(() => {
    if (!floorData?.length || !smartGuardRoute) return;
    fetchGuardTourData();
  }, [floorData, smartGuardRoute, liveDateRange]);

  useEffect(() => {
    if (!floorData?.length || !smartGuardRoute) return () => { };
    const timeout = setTimeout(fetchSelectedFloorGuardTourData, 2000);
    return () => clearTimeout(timeout);
  }, [sensors, selectedFloor, liveDateRange]);

  useEffect(() => {
    if (!liveDateRange?.start || !liveDateRange?.end) return () => { };
    if (smartGuardRoute || movingItem) return () => { };
    if (!selectedBuildingId || !selectedFloor?.LocationId) return () => { };

    let cancelled = false;
    const floorIdAtRequest: string = selectedFloor.LocationId;

    getAssets({
      locationId: selectedBuildingId,
      floorId: floorIdAtRequest,
      enableSubLocationsLookUp: true,
    }).then((response) => {
      if (cancelled || selectedFloor?.LocationId !== floorIdAtRequest) return;
      const latestSensorsById: Map<string, AssetInstance> = new Map(
        ((response?.assets ?? []) as AssetInstance[])
          .filter((asset: AssetInstance) => (
            asset?.type === TemplateType.SENSOR || asset?.type === TemplateType.METER
          ))
          .map((asset: AssetInstance): [string, AssetInstance] => [asset.id, asset]),
      );
      if (!latestSensorsById.size) return;

      setSensors((prevSensors) => prevSensors.map((sensor: AssetInstance) => {
        const latestSensor = latestSensorsById.get(sensor.id);
        if (!latestSensor) return sensor;

        const nextSensor = {
          ...sensor,
          lastDevicePayload: latestSensor.lastDevicePayload,
        };
        hydrateSensorMetricsFromPayload(nextSensor);
        return nextSensor;
      }));
    }).catch(() => { });

    return () => {
      cancelled = true;
    };
  }, [
    liveDateRange,
    movingItem,
    selectedBuildingId,
    selectedFloor?.LocationId,
    smartGuardRoute,
  ]);

  useEffect(() => {
    currentFloorIdRef.current = selectedFloor?.LocationId ?? null;
  }, [selectedFloor?.LocationId]);

  useEffect(() => {
    if (movingItem) return;
    setAreaUpdated(!areaUpdated);
    setAssets([]);
    setSensors([]);
    const sensorsArray = [];
    const assetsArray = [];
    const floorIdAtRequest: string | null = selectedFloor?.LocationId ?? null;
    floorAssetsRequestIdRef.current += 1;
    const requestId: number = floorAssetsRequestIdRef.current;

    const isStaleRequest = (): boolean => (
      requestId !== floorAssetsRequestIdRef.current
      || currentFloorIdRef.current !== floorIdAtRequest
    );

    if (selectedBuildingId && selectedFloor) {
      const locationId: string = selectedBuildingId;
      getAssets({
        locationId,
        floorId: selectedFloor?.LocationId,
        enableSubLocationsLookUp: true,
      }).then(async (response) => {
        if (isStaleRequest()) return;
        let guardTourData = null;
        if (selectedFloor.ExternalId) {
          guardTourData = await GetGuardTourData({ externalId: selectedFloor.ExternalId });
        }
        if (isStaleRequest()) return;
        const pirData = guardTourData?.pirData;
        if (pirData && selectedFloor) {
          let latestPirTimestamp: Date | null = null;
          selectedFloor.motionDetected = false;

          Object.keys(pirData).forEach((pointId) => {
            const pointTimestamp: string | undefined = pirData[pointId]?.timestamp;
            if (pointTimestamp) {
              const timestampDate = new Date(pointTimestamp);
              if (!latestPirTimestamp || timestampDate > latestPirTimestamp) {
                latestPirTimestamp = timestampDate;
              }
            }
            if (pirData[pointId]?.pointValue === 'Active') {
              selectedFloor.motionDetected = true;
            }
          });

          if (latestPirTimestamp) {
            selectedFloor.guardLastChecked = smartGuardTimestamp(latestPirTimestamp.toISOString());
          }
          setGuardLastUpdated(Moment());
        }
        if (!response?.assets?.length) {
          if (!isStaleRequest()) setLoading(false);
          return;
        }
        (response?.assets ?? []).forEach(async (asset) => {
          if (isStaleRequest()) return;
          let hasXY = false;
          asset.customFields.forEach((field) => {
            if (field.name === 'GeoX') {
              asset.GeoX = Number.parseInt(field.value, 10);
              if (asset.GeoX > 0) hasXY = true;
            }
            if (field.name === 'GeoY') {
              asset.GeoY = Number.parseInt(field.value, 10);
              if (asset.GeoY > 0) hasXY = true;
            }
          });
          asset.hasXY = hasXY;
          if (asset.type === TemplateType.SENSOR || asset.type === TemplateType.METER) {
            const foundLabel = (asset.labels ?? []).find((label) => labelTypeMap[label]);
            if (foundLabel) {
              asset.type = labelTypeMap[foundLabel];
            }
            if (asset.type === 'pir' || asset.type === 'pcs') {
              if (pirData) {
                const point = pirData[asset.id];
                if (point?.timestamp) {
                  asset.lastDetected = smartGuardTimestamp(point.timestamp);
                }
                asset.motionDetected = point?.pointValue === 'Active';
              }
            } else if (asset.type === 'electric') {
              // eslint-disable-next-line no-unused-vars
              // const data = await GetUtilitiesMetricByDate(range, asset.thingId ?? asset.id);
              const data = await GetMetric({
                metric: 'energy_accumulator',
                deviceNumId: asset.thingId ?? asset.id,
                range,
                interval: '30m',
              });
              if (data) {
                asset.energyUsage = data[data.length - 1]?.value ?? 0;
                asset.timestamp = data[data.length - 1]?.timestamp;
              }
              // Working on electricity data for map
              // console.log(data);
            } else if (asset.type === 'water') {
              // eslint-disable-next-line no-unused-vars
              // const data = await GetUtilitiesMetricByDate(range, asset.thingId ?? asset.id);
              const data = await GetMetric({
                metric: 'water_accumulator',
                deviceNumId: asset.thingId ?? asset.id,
                range,
                interval: '30m',
              });
              if (data) {
                asset.waterUsage = data[data.length - 1].value ?? 0;
                asset.timestamp = data[data.length - 1].timestamp;
              }
              // Working on water data for map
              // console.log(data);
            } else {
              const payloadHydrated = hydrateSensorMetricsFromPayload(asset);
              if (!payloadHydrated) {
                const data = await GetSensorData(
                  sensorRange,
                  asset.thingId ?? asset.id,
                );
                // if (asset.name.includes('TPS-6')) console.log(asset);
                asset.temp = Math.round((data[Metric.TEMPERATURE] ?? 0) * 100) / 100;
                asset.humidity = Math.round((data[Metric.HUMIDITY] ?? 0) * 100)
                  / 100;
                asset.co2 = Math.round((data[Metric.CO2] ?? 0) * 100) / 100;
                asset.tvoc = Math.round((data[Metric.TVOC] ?? 0) * 100) / 100;
                asset.pm10 = Math.round((data[Metric.PM10] ?? 0) * 100) / 100;
                asset.pm25 = Math.round((data[Metric.PM25] ?? 0) * 100) / 100;
                asset.spl = Math.round((data[Metric.NOISE] ?? 0) * 100) / 100;
                asset.lux = Math.round((data[Metric.LIGHT] ?? 0) * 100) / 100;
                asset.timestamp = data.timestamp;
              }
            }
            sensorsArray.push(asset);
            if (!isStaleRequest()) setSensors([...sensorsArray]);
          } else {
            assetsArray.push(asset);
            if (!isStaleRequest()) setAssets([...assetsArray]);
            if ((route.params as any)?.placeAsset && !asset.hasXY) {
              if (asset.id === (route.params as any)?.assetId) {
                setEditMode(true);
                setMovingItem(true);
                setDrawerOpen(false);
                setDrawMode(false);
                asset.hasXY = true;
                if (selectedArea) {
                  asset.GeoY = selectedArea.pinY;
                  asset.GeoX = selectedArea.pinX;
                  asset.initialPlacement = true;
                } else {
                  asset.GeoY = 120;
                  asset.GeoX = 20;
                }
                setMovingAssetId(asset.id);
                asset.locationFloor = selectedFloor?.Name;
              }
            }
          }
        });
      }).catch(() => {
        if (!isStaleRequest()) setLoading(false);
      });
    }
  }, [
    selectedBuildingId,
    selectedFloor?.LocationId,
    selectedArea?.LocationId,
    selectedSpace?.LocationId,
    route,
    movingItem,
  ]);

  // get Templates
  useEffect(() => {
    getTemplateGroups().then((response) => {
      if (response?.groups?.length) {
        const templateDataList: Template[] = response?.groups?.reduce((prev, group) => {
          if (group?.buttonPrototypes?.length) return prev.concat(group.buttonPrototypes);
          return prev;
        }, []) ?? [];
        setTemplates(templateDataList);
      }
    });
  }, []);

  useEffect(() => {
    setEditingShape(false);
    let cancelled = false;

    if (!selectedFloor) {
      setAreas([]);
      setLoading(false);
    } else {
      getAreas(selectedFloor.LocationId).then(async (response) => {
        const newAreas = [];
        response?.locations?.forEach((location: Area) => {
          const pLabelPoints = location.Points[0]?.X
            ? [location.Points.map((p) => [p.X, p.Y])]
            : [polygonToPoints(location.Polygon).map((p) => [p.x, p.y])];

          const p = polylabel(pLabelPoints);

          // Handle missing Points by generating from Polygon if needed
          const points = location.Points[0]?.X
            ? location.Points.map((point, index) => ({
              x: point.X,
              y: point.Y,
              number: point.Number ?? index,
            }))
            : polygonToPoints(location.Polygon);

          newAreas.push({
            name: location.Name,
            top: location.Top,
            pinY: p[1] - 22,
            bottom: location.Bottom,
            left: location.Left,
            pinX: p[0] - 18,
            right: location.Right,
            polygon: location.Polygon,
            highPriority: location.IsHighPriority,
            points,
            ParentId: selectedFloor.LocationId,
            LocationId: location.LocationId,
            spaceCount: location.spaceCount,
            assetCount: location.assetCount,
            sensorCount: location.sensorCount,
            meterCount: location.meterCount,
            assetCountByLabel: location.assetCountByLabel,
            LocationSubtype: location.LocationSubtype,
            LocationCapacity: location.LocationCapacity,
            ExternalId: location.ExternalId,
          });
        });
        if (buildingScreenRange.selectedRange === 7) {
          const liveOccupancyByArea = await Promise.all(newAreas.map(async (area) => {
            const metricData = await GetMetric({
              metric: Metric.OCCUPANCY,
              range: buildingScreenRange,
              aggregationFunction: AggregationFunction.MAX,
              externalId: area.ExternalId ?? area.name,
            }).catch(() => null);
            const occupancyValues: number[] = (metricData ?? [])
              .map((point) => Number(point?.value))
              .filter((value): value is number => Number.isFinite(value));
            const latestOccupancy: number | null = occupancyValues.length > 0
              ? occupancyValues[occupancyValues.length - 1]
              : null;
            return {
              locationId: area.LocationId,
              occupancy: latestOccupancy,
            };
          }));
          if (cancelled) return;
          const occupancyByLocationId: Map<string, number | null> = new Map(
            liveOccupancyByArea.map((item) => [item.locationId, item.occupancy]),
          );
          newAreas.forEach((area) => {
            const occupancy = occupancyByLocationId.get(area.LocationId);
            if (!Number.isFinite(occupancy)) return;
            area.occupancy = occupancy;
            area.occupancyPercentage = area.LocationCapacity
              ? occupancy / area.LocationCapacity
              : '-';
          });
        } else {
          const averageUtilizationData = await GetAverageUtilization({
            range: buildingScreenRange, parentLocationId: selectedFloor.ExternalId,
          });
          if (cancelled) return;
          newAreas.forEach((area) => {
            const utilization = averageUtilizationData[area.ExternalId];
            if (utilization !== undefined && utilization !== null) {
              area.occupancy = utilization * area.LocationCapacity;
              area.occupancyPercentage = area.LocationCapacity ? utilization : '-';
            }
          });
        }
        setAreas(newAreas);
        if (selectedArea
          && !newAreas.map((area: Area) => area?.LocationId).includes(selectedArea?.LocationId)) {
          setSelectedArea(null);
          setSelectedSpace(null);
        }
      }).finally(() => {
        if (!cancelled) setLoading(false);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [
    selectedFloor,
    areaUpdated,
    buildingScreenRange.start,
    buildingScreenRange.end,
    buildingScreenRange.selectedRange,
  ]);

  useEffect(() => {
    if (!selectedArea) return;
    getSpaces(selectedArea?.LocationId).then((response) => {
      const newSpaces = [];
      response?.locations?.forEach((location: Space) => {
        newSpaces.push({
          name: location.Name,
          top: location.Top,
          bottom: location.Bottom,
          left: location.Left,
          right: location.Right,
          polygon: location.Polygon,
          highPriority: location.IsHighPriority,
          points: location.Points.map((point, index) => ({
            x: point.X,
            y: point.Y,
            number: point.Number ?? index,
          })),
          ParentId: selectedArea.LocationId,
          LocationId: location.LocationId,
          assetCount: location.assetCount,
          sensorCount: location.sensorCount,
          meterCount: location.meterCount,
          LocationSubtype: location.LocationSubtype,
          LocationCapacity: location.LocationCapacity,
        });
      });
      setSpaces(newSpaces);
    });
  }, [selectedArea]);

  useEffect(() => {
    if (!selectedArea?.LocationId || !areas.length) return;
    const updatedArea = areas.find((area) => area?.LocationId === selectedArea.LocationId);
    if (updatedArea && updatedArea !== selectedArea) setSelectedArea(updatedArea);
  }, [areas, selectedArea?.LocationId]);

  useEffect(() => {
    if (!selectedSpace?.LocationId || !spaces.length) return;
    const updatedSpace = spaces.find((space) => space?.LocationId === selectedSpace.LocationId);
    if (updatedSpace && updatedSpace !== selectedSpace) setSelectedSpace(updatedSpace);
  }, [spaces, selectedSpace?.LocationId]);

  useEffect(() => {
    if (!occupancySelected || !selectedArea?.LocationId) return;
    const matchedArea = areas.find((area) => area?.LocationId === selectedArea.LocationId);
    if (matchedArea && !hasPcsAssets(matchedArea)) {
      setSelectedArea(null);
      setSelectedSpace(null);
    }
  }, [occupancySelected, selectedArea?.LocationId, areas]);

  useEffect(() => {
    if (drawerOpen) {
      drawerInset.value = withTiming(400, { duration: 200 });
    } else {
      drawerInset.value = withTiming(showNewSidebar ? 63 : 0, { duration: 200 });
    }
  }, [drawerOpen, showNewSidebar]);

  const applyZoomAtCenter = (newScale: number) => {
    const previousScale = savedScale.value;
    if (previousScale === newScale) return;

    const centerX = viewWidth.value / 2;
    const centerY = viewHeight.value / 2;
    const ratio = newScale / previousScale;

    translateX.value = withTiming(centerX - ((centerX - translateX.value) * ratio));
    translateY.value = withTiming(centerY - ((centerY - translateY.value) * ratio));

    savedScale.value = newScale;
    setScaleLabel(newScale * 100);
    scale.value = withTiming(newScale);
  };

  const zoomIn = () => {
    let newScale = 1;
    setZoomOutDisabled(false);
    setZoomInDisabled(false);
    if (!drawMode) {
      if (savedScale.value === 2.25) {
        newScale = 2.5;
        setZoomInDisabled(true);
      } else if (savedScale.value === 2) newScale = 2.25;
      else if (savedScale.value === 1.75) newScale = 2;
      else if (savedScale.value === 1.5) newScale = 1.75;
      else if (savedScale.value === 1.25) newScale = 1.5;
      else if (savedScale.value === 1) newScale = 1.25;
      else if (savedScale.value === 0.75) newScale = 1;
      else if (savedScale.value === 0.5) newScale = 0.75;
      else if (savedScale.value === 0.25) newScale = 0.5;
      applyZoomAtCenter(newScale);
    }
  };

  const zoomOut = () => {
    let newScale = 1;
    setZoomOutDisabled(false);
    setZoomInDisabled(false);
    setClusteringDisabled(false);
    if (!drawMode) {
      if (savedScale.value === 0.25) {
        setZoomOutDisabled(true);
        setZoomInDisabled(false);
      } else if (savedScale.value === 2.5) newScale = 2.25;
      else if (savedScale.value === 2.25) newScale = 2;
      else if (savedScale.value === 2) newScale = 1.75;
      else if (savedScale.value === 1.75) newScale = 1.5;
      else if (savedScale.value === 1.5) newScale = 1.25;
      else if (savedScale.value === 1.25) newScale = 1;
      else if (savedScale.value === 1) newScale = 0.75;
      else if (savedScale.value === 0.75) newScale = 0.5;
      else if (savedScale.value === 0.5) {
        setZoomOutDisabled(true);
        setZoomInDisabled(false);
        newScale = 0.25;
      }
      applyZoomAtCenter(newScale);
    }
  };

  const handleKeyDown = (event) => {
    if (!movingItem) {
      if (!drawModeRef.current && !movingItemRef.current) {
        if (event.key === '+' || event.key === '=') {
          zoomIn();
        } else if (event.code === 'Minus') {
          zoomOut();
        }
      }
    }
  };

  const handleWheel = (e) => {
    if (!drawModeRef.current && !movingItemRef.current) {
      if (e.deltaY < 0 && (e.ctrlKey || e.metaKey)) {
        zoomIn();
      } else if (e.ctrlKey || e.metaKey) {
        zoomOut();
      }
    }
  };

  useEffect(() => {
    getUserGroups().then((response) => {
      if (response?.UserGroups?.length > 0) setUserGroups(response.UserGroups);
    });
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('wheel', handleWheel, true);
    return window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);

  useEffect(() => {
    movingItemRef.current = movingItem;
  }, [movingItem]);

  const resetTicketsFilter = () => {
    setFilterOpen(false);
    setOtherPriorityFilter(false);
    setLowPriorityFilter(false);
    setMediumPriorityFilter(false);
    setHighPriorityFilter(false);
    setCriticalPriorityFilter(false);
    setDoneFilter(false);
    setInProgressFilter(false);
    setNotStartedFilter(false);
    setSelectedLocation(null);
    setSelectedAssetId('');
  };

  function getNegativeScale() {
    switch (scaleLabel) {
      case 25: return 2;
      case 50: return 1.5;
      case 125: return 0.80;
      case 150: return 0.70;
      case 175: return 0.65;
      case 200: return 0.55;
      case 225: return 0.50;
      case 250: return 0.45;
      default: return 1;
    }
  }

  const resetScale = () => {
    savedScale.value = withTiming(1);
    negativeScale.value = withTiming(1);
    setScaleLabel(100);
    scale.value = withTiming(1);
    setZoomInDisabled(false);
    setZoomOutDisabled(false);
    translateX.value = 0;
    translateY.value = 0;
  };

  const panGesture = Gesture.Pan()
    .onChange((event) => {
      translateX.value += event.changeX;
      translateY.value += event.changeY;
    }).activeCursor('grab').runOnJS(true);

  const tapGesture = Gesture.Tap()
    .onEnd((event) => {
      if (drawMode && !selectedSpace) {
        let clash = false;
        if (areaComplete) {
          showAlert(
            `${selectedSpace ? 'Space' : 'Area'} incomplete`,
            false,
            () => { },
            'Continue bounding',
            'Please complete the area creation process',
            'Discard and leave',
            () => {
              setDrawPoints([]);
              setAreaComplete(false);
              setDrawMode(false);
              setEditingShape(false);
              const newAreas = Object.assign([], areas);
              const newSpaces = Object.assign([], spaces);
              if (beingEdited !== null) {
                if (selectedArea) {
                  newSpaces.push(beingEdited);
                  setSpaces(newSpaces);
                } else {
                  newAreas.push(beingEdited);
                  setAreas(newAreas);
                }
                setBeingEdited(null);
              }
            },
          );
          return;
        }
        drawPoints.forEach((point) => {
          if (Math.hypot(point.x - event.x, point.y - event.y) < 20) {
            clash = true;
          }
        });
        if (!clash && selectedArea) {
          const { x, y } = event;
          let inside = false;
          for (
            let i = 0, j = selectedArea.points.length - 1;
            i < selectedArea.points.length;
            // eslint-disable-next-line no-plusplus
            j = i++
          ) {
            const xi = selectedArea.points[i].x;
            const yi = selectedArea.points[i].y;
            const xj = selectedArea.points[j].x;
            const yj = selectedArea.points[j].y;

            const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
            if (intersect) inside = !inside;
          }

          if (inside) {
            setDrawPoints([...drawPoints, { x: event.x, y: event.y, number: drawPoints.length }]);
          } else {
            showAlert(POINT_WITHIN_AREA_ERROR);
          }
        } else if (!clash) {
          setDrawPoints([...drawPoints, { x: event.x, y: event.y, number: drawPoints.length }]);
        }
        if (
          drawPoints.length > 1
          && drawPoints[0]
          && Math.hypot(drawPoints[0].x - event.x, drawPoints[0].y - event.y) < 20
          && drawPoints.length > 2
        ) {
          setAreaComplete(true);
        }
      }
    });

  const transformStyle = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [
      {
        translateX: translateX.value,
      },
      {
        translateY: translateY.value,
      },
    ],
  }));

  const drawerStyle = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [
      {
        translateX: drawerInset.value,
      },
      {
        scale: negativeScale.value,
      },
    ],
  }));

  const sensorStyle = useAnimatedStyle(() => ({
    opacity: focusedAssetId.value ? 0.2 : 1,
  }));

  const negativeScaleStyle = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [
      { scale: getNegativeScale() },
    ],
  }));

  const loadSavedFilters = async () => {
    await AsyncStorage.getItem('saveFilterToggled').then((val) => {
      setSaveFilterToggled(val === 'yes');
      if (val !== 'yes') {
        dispatch(resetAllTicketFilters());
      }
    });
    setSavedFiltersLoaded(true);
  };

  const resetDrawState = () => {
    setDrawPoints([]);
    setSelectedTool('draw');
    setBeingEdited(null);
    setEditingShape(false);
    setAreaComplete(false);
    setDrawMode(false);
    setCreateModalVisible(false);
    setSaving(false);
    setShapeName('');
    setHighPriority(false);
    setLocationSubtype(null);
    setLocationCapacity(null);
    setLocationExternalId(null);
  };

  const renderFloorButton = (floor, key) => {
    const selected: boolean = selectedFloor?.LocationId === floor?.LocationId;
    const backgroundColor: string = selected ? Colors.light.hereworksBlue500 : '#FFF';
    return <Pressable
      testID={`floorButton#${key}`}
      style={({ hovered }: PressableState) => [
        Style.zoomButton,
        {
          borderColor: hovered ? Colors.light.hereworksBlue500 : 'transparent',
          marginHorizontal: 6,
          backgroundColor,
        },
      ]}
      disabled={selected}
      onPress={() => {
        logEvent('Map', `floor_${key}`);
        setZoomInDisabled(false);
        setZoomOutDisabled(false);
        resetTicketsFilter();
        setSelectedSpace(null);
        setSelectedArea(null);
        setSelectedFloor(floor);
        setLoading(true);
        setDrawPoints([]);
        setRedoPoints([]);
        setAreaComplete(false);
      }}
      key={key}
    >
      <Text
        style={[
          styles.buttonText,
          { color: selected ? '#FFF' : Colors.light.hereworksBlack600 },
        ]}
      >
        {floor.Number ?? '-'}
      </Text>
      {smartGuardRoute ? <View
        style={[styles.smartGuardDot, {
          backgroundColor: floor.motionDetected ? 'red' : 'green',
        }]} /> : null}
    </Pressable>;
  };

  function getPointsString(points) {
    let pointsString = '';
    points.forEach(
      // eslint-disable-next-line no-return-assign
      (point, index) => (pointsString += `${index === 0 ? '' : ', '}${point.x} ${point.y}`),
    );
    if (selectedTool === 'ellipse') pointsString += ', ellipse';
    return pointsString;
  }

  function getMinY(points) {
    let minY = points[0].y;
    points.forEach((point) => {
      if (point.y < minY) {
        minY = point.y;
      }
    });

    return minY;
  }

  function getMinX(points) {
    let minX = points[0].x;
    points.forEach((point) => {
      if (point.x < minX) {
        minX = point.x;
      }
    });

    return minX;
  }

  function getMaxY(points) {
    let maxY = points[0].y;
    points.forEach((point) => {
      if (point.y > maxY) maxY = point.y;
    });

    return maxY;
  }

  function getMaxX(points) {
    let maxX = points[0].x;
    points.forEach((point) => {
      if (point.x > maxX) maxX = point.x;
    });

    return maxX;
  }

  function isPointInShape(x, y, assetLocationPoints) {
    const points = assetLocationPoints ?? selectedSpace?.points ?? selectedArea?.points ?? [];
    if (points.length === 0) return true;
    let inside = false;
    for (
      let i = 0, j = points.length - 1;
      i < points.length;
      // eslint-disable-next-line no-plusplus
      j = i++
    ) {
      const xi = points[i].x;
      const yi = points[i].y;
      const xj = points[j].x;
      const yj = points[j].y;

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  const ZoomInButton = () => (
    <Pressable
      style={(state) => [
        Style.zoomButton,
        { opacity: (zoomInDisabled || drawMode || state.hovered) ? 0.5 : 1 },
        { marginBottom: 4 },
      ]}
      onPress={() => {
        logEvent('Map', 'zoom_in');
        zoomIn();
      }}
      disabled={zoomInDisabled || drawMode}
      testID='map-zoom-in-btn'>
      <PlusIcon color={Colors.light.hereworksBlue500} />
    </Pressable>
  );

  function editorButtonPress(): void {
    if (editMode) setMovingItem(false);
    if (occupancySelected) setSelectedTab('assets');
    if (editMode && drawMode) {
      setEditMode(!editMode);
      setDrawMode(false);
      setDrawPoints([]);
      setEditingShape(false);
      const newAreas = Object.assign([], areas);
      const newSpaces = Object.assign([], spaces);
      if (beingEdited !== null) {
        if (selectedArea) {
          newSpaces.push(beingEdited);
          setSpaces(newSpaces);
        } else {
          newAreas.push(beingEdited);
          setAreas(newAreas);
        }
      }
    } else setEditMode(!editMode);
    if (movingAssetId) {
      setMovingAssetId(null);
      navigation.setParams({ placeAsset: false, assetId: null } as any);
    }
    logEvent('Map', 'map_editor');
  }

  const ZoomOutButton = () => (
    <Pressable
      style={(state) => [Style.zoomButton, {
        opacity: (zoomOutDisabled || drawMode || state.hovered) ? 0.5 : 1,
        marginTop: 0,
      }]}
      onPress={() => {
        logEvent('Map', 'zoom_out');
        zoomOut();
      }}
      disabled={zoomOutDisabled || drawMode}
      testID='map-zoom-out-btn'>
      <MinusIcon />
    </Pressable>
  );

  const GrabHandle = ({ rotate }) => (
    <Pressable style={(state) => ({
      transform: [
        { rotate: rotate ? '90deg' : '0deg' },
      ] as const,
      backgroundColor: state.hovered ? Colors.light.hereworksBlue500
        : Colors.light.hereworksBlue300,
      width: 24,
      height: 24,
      borderRadius: 12,
    })}>
      <ExpandIcon style={{
        transform: [{ scale: 0.7 }],
      }} color="#fff" />
    </Pressable>
  );

  const DrawLines = () => (
    <Svg
      style={{
        zIndex: 1,
        width: '100%',
        height: '100%',
        position: 'absolute',
      }}
      pointerEvents='none'
    >
      {drawPoints.map((point, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Line
              key={index}
              x1={drawPoints[index - 1].x}
              x2={point.x}
              y1={drawPoints[index - 1].y}
              y2={point.y}
              stroke={drawMode ? Colors.light.hereworksOrange500 : Colors.light.hereworksBlue200}
              strokeWidth={drawMode ? 2 : 1}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {selectedTool !== 'ellipse' && <Polygon
            points={getPointsString(drawPoints)}
            fill={Colors.light.hereworksBlue200}
            opacity={0.2}
            testID={`map-drawing-point#${index + 1}`}
          />}
        </React.Fragment>
      ))}
    </Svg>
  );

  const DrawLineGrabs = () => {
    const sharedPoints = drawPoints.map((p) => ({
      x: useSharedValue(p.x),
      y: useSharedValue(p.y),
    }));

    const animatedPoints = useDerivedValue(() => sharedPoints
      .map((p) => `${p.x.value},${p.y.value}`)
      .join(' '));

    const animatedPolygonProps = useAnimatedProps(() => ({
      points: animatedPoints.value,
    }));

    const ellipseBounds = useDerivedValue(() => {
      if (sharedPoints.length < 4) {
        return {
          cx: 0, cy: 0, rx: 0, ry: 0,
        };
      }

      const p0 = sharedPoints[0];
      const p1 = sharedPoints[1];
      const p2 = sharedPoints[2];
      const p3 = sharedPoints[3];

      const cx = (p0.x.value + p2.x.value) / 2;
      const cy = (p0.y.value + p2.y.value) / 2;

      const vectorA = {
        x: p1.x.value - p0.x.value,
        y: p1.y.value - p0.y.value,
      };
      const vectorB = {
        x: p3.x.value - p0.x.value,
        y: p3.y.value - p0.y.value,
      };

      const rx = Math.sqrt(vectorB.x ** 2 + vectorB.y ** 2) / 2;
      const ry = Math.sqrt(vectorA.x ** 2 + vectorA.y ** 2) / 2;

      return {
        cx, cy, rx, ry,
      };
    });

    const animatedEllipseProps = useAnimatedProps(() => ({
      cx: ellipseBounds.value.cx,
      cy: ellipseBounds.value.cy,
      rx: ellipseBounds.value.rx,
      ry: ellipseBounds.value.ry,
    }));

    return (
      <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
        <Svg
          style={{
            zIndex: -1,
            width: '100%',
            height: '100%',
          }}
          pointerEvents='none'
        >
          <AnimatedPolygon
            animatedProps={animatedPolygonProps}
            stroke={Colors.light.hereworksOrange500}
            strokeDasharray={[5, 5]}
            strokeWidth={2}
            fill="none"
          />
          {selectedTool === 'ellipse' && <AnimatedEllipse
            animatedProps={animatedEllipseProps}
            stroke={Colors.light.hereworksOrange500}
            strokeDasharray={[5, 5]}
            strokeWidth={2}
            fill="none"
          />}
        </Svg>
        {sharedPoints.map((point, index) => {
          if (index === 0 || index === sharedPoints.length) return null;

          const prev = sharedPoints[index - 1];

          const gesture = Gesture.Pan()
            .onChange((e) => {
              prev.x.value += e.changeX;
              prev.y.value += e.changeY;
              point.x.value += e.changeX;
              point.y.value += e.changeY;

              if (index === sharedPoints.length - 1) {
                sharedPoints[0].x.value = point.x.value;
                sharedPoints[0].y.value = point.y.value;
              }

              if (index === 1) {
                const lastIndex = sharedPoints.length - 1;
                sharedPoints[lastIndex].x.value = sharedPoints[0].x.value;
                sharedPoints[lastIndex].y.value = sharedPoints[0].y.value;
              }
            })
            .onEnd(() => {
              const updated = sharedPoints.map((pt, i) => ({
                x: pt.x.value,
                y: pt.y.value,
                number: i,
              }));
              if (index === sharedPoints.length - 1) {
                updated[0] = { x: point.x.value, y: point.y.value, number: 0 };
              } else if (index === 1) {
                updated[updated.length - 1] = {
                  x: updated[0].x,
                  y: updated[0].y,
                  number: updated.length - 1,
                };
              }
              setRedoPoints([]);
              setDrawPoints(updated);
            });

          const animatedStyle = useAnimatedStyle(() => {
            const midX = (prev.x.value + point.x.value) / 2;
            const midY = (prev.y.value + point.y.value) / 2;
            return {
              position: 'absolute',
              left: midX - 12,
              top: midY - 12,
            };
          });

          return (
            <GestureDetector gesture={gesture} key={`grab_${index}`}>
              <Animated.View style={[styles.expandPoint, animatedStyle]}>
                <GrabHandle rotate={index % 2 === 0} />
              </Animated.View>
            </GestureDetector>
          );
        })}
      </View>
    );
  };

  const getAreaForItem = useCallback((item: any) => {
    const directAreaId = String(item?.areaId ?? item?.AreaId ?? '');
    if (directAreaId.length > 0) {
      const byAreaId = (areas ?? []).find(
        (area: any) => String(area?.LocationId ?? '') === directAreaId,
      );
      if (byAreaId) return byAreaId;
    }

    const locationId = String(item?.locationId ?? item?.LocationId ?? '');
    if (!locationId.length) return null;

    const areaByLocationId = (areas ?? []).find(
      (area: any) => String(area?.LocationId ?? '') === locationId,
    );
    if (areaByLocationId) return areaByLocationId;

    const matchedSpace = (spaces ?? []).find(
      (space: any) => String(space?.LocationId ?? '') === locationId,
    );
    if (!matchedSpace?.ParentId) return null;

    return (areas ?? []).find(
      (area: any) => String(area?.LocationId ?? '') === String(matchedSpace.ParentId),
    ) ?? null;
  }, [areas, spaces]);

  const getInitialPlacementForItem = useCallback((item: any): { x: number, y: number } => {
    const fallback = { x: 20, y: 120 };
    const selectedAreaPoint = {
      x: Number(selectedArea?.pinX),
      y: Number(selectedArea?.pinY),
    };
    if (Number.isFinite(selectedAreaPoint.x) && Number.isFinite(selectedAreaPoint.y)) {
      return selectedAreaPoint;
    }

    const selectedSpacePoint = {
      x: Number(selectedSpace?.pinX),
      y: Number(selectedSpace?.pinY),
    };
    if (Number.isFinite(selectedSpacePoint.x) && Number.isFinite(selectedSpacePoint.y)) {
      return selectedSpacePoint;
    }

    const matchedArea = getAreaForItem(item);
    const matchedPoint = {
      x: Number(matchedArea?.pinX),
      y: Number(matchedArea?.pinY),
    };
    if (Number.isFinite(matchedPoint.x) && Number.isFinite(matchedPoint.y)) {
      return matchedPoint;
    }

    return fallback;
  }, [
    getAreaForItem,
    selectedArea?.pinX,
    selectedArea?.pinY,
    selectedSpace?.pinX,
    selectedSpace?.pinY,
  ]);

  const renderAssetRow = (asset, index: number, isSensor: boolean = false) => {
    if (searchValue?.trim().length
      && !(asset.name?.toLowerCase().includes(searchValue?.toLowerCase())
        || asset.id?.toLowerCase().includes(searchValue?.toLowerCase()))) return null;

    if (!drawerOpen) return null;

    return (
      <Pressable
        onHoverIn={() => {
          if (!movingAssetId && !isSensor) focusedAssetId.value = asset.id;
        }}
        onHoverOut={() => {
          if (!movingAssetId && !isSensor) focusedAssetId.value = null;
        }}
        style={{ zIndex: index * -1 }}>
        <AssetRow
          key={`asset_${asset.id}`}
          {...{
            index, asset, editMode, setMovingItem, showAlert, isSensor, drawerOpen,
          }}
          editPosition={() => {
            setDrawerOpen(false);
            setMovingItem(true);
            setMovingAssetId(asset.id);
          }}
          move={() => {
            setMovingItem(true);
            setDrawerOpen(false);
            setDrawMode(false);
            asset.hasXY = true;
            if (isSensor) {
              const sensorArea = getAreaForItem(asset);
              if (sensorArea) {
                setSelectedSpace(null);
                setSelectedArea(sensorArea);
              }
            }
            const initialPlacement = getInitialPlacementForItem(asset);
            asset.GeoY = initialPlacement.y;
            asset.GeoX = initialPlacement.x;
            centerViewOnCoordinate({ x: initialPlacement.x, y: initialPlacement.y }, true);
            setMovingAssetId(asset.id);
            asset.locationFloor = selectedFloor?.Name;
          }}
          removeAsset={() => {
            const newArr = Object.assign([], assetsSelected ? assets : sensors);
            newArr.forEach((a) => {
              if (asset.id === a.id) {
                delete a.GeoX;
                delete a.GeoY;
                a.hasXY = false;
                a.customFields = a.customFields.filter(
                  (field) => field.name !== 'GeoX' && field.name !== 'GeoY',
                );
              }
            });
            if (assetsSelected) setAssets(newArr);
            else setSensors(newArr);
          }}
          hideDrawer={() => { }}
          assetContextIndex={assetContextIndex}
          setAssetContextIndex={setAssetContextIndex}
          focusedAssetId={focusedAssetId.value}
          sectionExpanded={!showAreaList}
          floorId={selectedFloor?.LocationId}
        />
      </Pressable>
    );
  };

  const TicketHeader = () => (
    <View style={{ flexDirection: 'row', flex: 1, paddingHorizontal: 24 }}>
      <View style={styles.ticketHeaderSub}>
        <DateComponent
          onChange={onConfirm}
          onCompareChange={() => { }}
          onConfirm={() => { }}
          onOpen={setDateOpen}
          startDate={range.start}
          endDate={range.end}
          testID='map-date-picker-btn'
        />
      </View>
      <View
        style={{
          flex: 1,
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        <Pressable style={styles.filterButton} onPress={() => setFilterOpen(!filterOpen)}>
          <Text style={styles.filterButtonTitle}>Filter</Text>
          <FilterIcon16 />
          {anyFilterApplied() && <View style={styles.filterDot} />}
        </Pressable>
      </View>
    </View>
  );

  const LocationHeaders = () => (
    <View
      style={{ flexDirection: 'row' }}
      testID='map-header'
    >
      {smartGuardRoute && (
        <Pressable
          style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 24 }}
          onPress={() => {
            setSmartGuardListView(true);
            setSelectedFloor(null);
          }}>
          <Text style={[styles.areaText, { marginStart: 16, marginEnd: 16 }]}>All floors</Text>
          <RightArrow />
        </Pressable>
      )}
      {selectedArea ? <>
        <Pressable
          onPress={() => {
            setSelectedSpace(null);
            setSelectedArea(null);
            setDrawPoints([]);
            setAreaComplete(false);
            setDrawMode(false);
            setLoading(true);
          }}
        >
          <Text style={[styles.areaText, { marginStart: 16 }]}>{selectedFloor?.Name ?? '-'}</Text>
        </Pressable>
        {selectedSpace ? <>
          <Pressable
            style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 24 }}
            onPress={() => setSelectedSpace(null)}
          >
            <RightArrow />
            <Text style={styles.areaText}>
              {selectedArea?.name}
            </Text>
          </Pressable>
          <View style={styles.locationHead}>
            <RightArrow />
            <Text style={styles.spaceText}>{selectedSpace?.name}</Text>
          </View>
        </> : <View style={styles.locationHead}>
          <RightArrow />
          <Text style={styles.spaceText}>{selectedArea?.name}</Text>
        </View>}
      </> : <Text style={[smartGuardRoute
        ? styles.spaceText : styles.areaText, { marginStart: smartGuardRoute ? 0 : 30 }]}>
        {selectedFloor?.Name ?? '-'}
      </Text>}
    </View>
  );

  const AreasAndSpaces = () => (
    <>
      {displayAreas.map((area, index) => {
        if (!area || !area.polygon || area.moving || (ticketsSelected && !showNewSidebar)
          || area?.ParentId !== selectedFloor?.LocationId
          || (selectedArea && area?.LocationId !== selectedArea?.LocationId)) {
          return null;
        }

        const areaMotionDetected = sensors.some(
          (sensor) => sensor.locationId === area.LocationId && sensor.motionDetected,
        );

        return (
          <React.Fragment key={index}>
            <AreaView
              occupancy={area.occupancyPercentage !== undefined
                ? area.occupancyPercentage * 100 : undefined}
              focused={focusedLocationId.value === area.LocationId}
              negativeScale={negativeScale.value}
              index={index}
              area={area}
              areas={areas}
              setAreas={setAreas}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              getMinX={getMinX}
              getMinY={getMinY}
              getMaxX={getMaxX}
              getMaxY={getMaxY}
              showAlert={showAlert}
              editMode={editMode}
              spaceSelected={selectedSpace}
              areaSelected={!selectedSpace
                && selectedArea?.LocationId === area.LocationId}
              drawMode={drawMode}
              areaUpdated={() => setAreaUpdated(!areaUpdated)}
              smartGuard={smartGuardRoute}
              motionDetected={areaMotionDetected}
              focusedLocationId={focusedLocationId}
              {...{ occupancyShown, ticketsSelected }}
            />
          </React.Fragment>
        );
      })}
      {selectedArea
        && spaces.map((space, index) => {
          if (!space || (selectedSpace && space.LocationId !== selectedSpace.LocationId)) {
            return null;
          }
          return (
            <React.Fragment key={space.LocationId}>
              <AreaView
                negativeScale={negativeScale.value}
                index={index}
                area={space}
                parentArea={selectedArea}
                areas={spaces}
                setAreas={setSpaces}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                getMinX={getMinX}
                getMinY={getMinY}
                getMaxX={getMaxX}
                getMaxY={getMaxY}
                showAlert={showAlert}
                editMode={editMode}
                isSpace={true}
                spaceSelected={selectedSpace}
                areaSelected={selectedSpace?.LocationId === space.LocationId}
                drawMode={drawMode}
                areaUpdated={() => setAreaUpdated(!areaUpdated)}
                {...{ occupancyShown }}
                focusedLocationId={focusedLocationId}
              />
            </React.Fragment>
          );
        })}
    </>
  );

  const areasAndSpacesPins = () => (
    subLocationsEnabled ? null : (
    <>
      {(() => {
        const PIN_OVERLAP_TOLERANCE = 20;
        const PIN_OVERLAP_NUDGE = 34;

        const visibleAreas = displayAreas.filter((area) => (
          area
          && !(ticketsSelected && !showNewSidebar)
          && area.polygon
          && area?.ParentId === selectedFloor?.LocationId
          && (!selectedArea || area?.LocationId === selectedArea?.LocationId)
        ));

        return visibleAreas.map((area, index) => {
          const overlapCount = visibleAreas.slice(0, index).filter((prevArea) => (
            Math.abs(prevArea.pinY - area.pinY) <= PIN_OVERLAP_TOLERANCE
            && Math.abs(prevArea.pinX - area.pinX) <= PIN_OVERLAP_TOLERANCE
          )).length;

          const dimOpacity = (movingItem)
            && focusedLocationId.value !== area.LocationId && movingLocationId !== area.LocationId;

          return (
            <Pressable
              style={(state) => [
                {
                  position: 'absolute',
                  top: area.pinY + (overlapCount * PIN_OVERLAP_NUDGE),
                  left: area.pinX + (overlapCount * PIN_OVERLAP_NUDGE),
                  opacity: dimOpacity ? 0.5 : 1,
                  zIndex: (state.hovered || pinMenuOpen === area.LocationId) ? 10 : 0,
                  cursor: 'auto',
                },
              ]}
              key={area.LocationId ?? index}
              onHoverIn={() => {
                if (movingItem) return;
                focusedLocationId.value = area.LocationId;
              }}
              disabled={movingItem}
              onHoverOut={() => {
                if (movingItem) return;
                focusedLocationId.value = null;
              }}
              testID='map-location-icon-on-the-map-btn'
            >
              <PinIcon
                ticketCount={tickets
                  ?.filter((t) => t.asset?.locationId === area.LocationId)?.length}
                capacity={area.LocationCapacity ?? 0}
                negativeScale={getNegativeScale()}
                index={index}
                area={area}
                areas={areas}
                setMovingItem={() => {
                  focusedLocationId.value = null;
                  setMovingLocationId(area.LocationId);
                  resetScale();
                  setMovingItem(true);
                }}
                movingLocationId={movingLocationId}
                finishedMovingArea={() => {
                  focusedLocationId.value = null;
                  setMovingItem(false);
                  setMovingLocationId(null);
                }}
                editArea={() => {
                  const areasCopy = Object.assign([], areas);
                  const areaToEdit = areasCopy.splice(index, 1);
                  setDrawPoints(areaToEdit[0]?.points ?? []);
                  setDrawMode(true);
                  setEditLocationType('area');
                  setEditLocationId(areaToEdit[0]?.LocationId);
                  setShapeName(areaToEdit[0]?.name ?? '');
                  setLocationSubtype(areaToEdit[0]?.LocationSubtype);
                  setLocationCapacity(areaToEdit[0]?.LocationCapacity);
                  setLocationExternalId(areaToEdit[0]?.ExternalId);
                  setHighPriority(areaToEdit[0]?.highPriority ?? false);
                  savedScale.value = 1;
                  setEditingShape(true);
                  setAreaComplete(true);
                  setBeingEdited(area);
                  if (areaToEdit[0]?.polygon?.includes('ellipse')) {
                    setSelectedTool('ellipse');
                  } else setSelectedTool('draw');
                }}
                setAreas={setAreas}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                getMinX={getMinX}
                getMinY={getMinY}
                getMaxX={getMaxX}
                getMaxY={getMaxY}
                getPointsString={getPointsString}
                showAlert={showAlert}
                editMode={editMode}
                arrowPress={() => {
                  logEvent('Map', 'pin_arrow');
                  setDrawerOpen(true);
                  setSelectedArea(area);
                  setShowAreaList(true);
                }}
                spaceSelected={selectedSpace}
                areaSelected={!selectedSpace
                  && selectedArea?.LocationId === area.LocationId}
                drawMode={drawMode}
                setPinMenuOpen={setPinMenuOpen}
                areaUpdated={() => setAreaUpdated(!areaUpdated)}
                occupancyShown={(occupancyShown && !smartGuardRoute)}
                parentFloor={selectedFloor}
                assetsSelected={assetsSelected}
                focusedLocationId={focusedLocationId}
                ticketsSelected={ticketsSelected}
                smartGuard={smartGuardRoute}
              />
            </Pressable>
          );
        });
      })()}
      {selectedArea
        && (() => {
          const PIN_OVERLAP_TOLERANCE = 2;
          const PIN_OVERLAP_NUDGE = 10;

          const visibleSpaces = spaces.filter((space) => (
            space && (!selectedSpace || space.LocationId === selectedSpace.ParentId)
          ));

          return visibleSpaces.map((space, index) => {
            const top = space.top + (((space.bottom - space.top) / 2) - 22);
            const left = space.left + (((space.right - space.left) / 2) - 18);

            const overlapCount = visibleSpaces.slice(0, index).filter((prevSpace) => {
              const prevTop = prevSpace.top + (((prevSpace.bottom - prevSpace.top) / 2) - 22);
              const prevLeft = prevSpace.left + (((prevSpace.right - prevSpace.left) / 2) - 18);
              return Math.abs(prevTop - top) <= PIN_OVERLAP_TOLERANCE
                && Math.abs(prevLeft - left) <= PIN_OVERLAP_TOLERANCE;
            }).length;

            return (
              <Pressable
                style={(state) => [
                  {
                    position: 'absolute',
                    zIndex: state.hovered ? 10 : 1,
                    top: top + (overlapCount * PIN_OVERLAP_NUDGE),
                    left: left + (overlapCount * PIN_OVERLAP_NUDGE),
                  },
                ]}
                key={space.LocationId ?? index}
                testID='map-location-icon-on-the-map-btn'
              >
                {({ hovered }: PressableState): ReactElement => (
                  <RNAnimated.View
                    key={index}
                    style={{
                      zIndex: hovered ? 10 : -1,
                      transform: [{ scale: negativeScale.value }],
                    }}
                  >
                    <PinIcon
                      ticketCount={tickets
                        ?.filter((t) => t.asset?.locationId === space.LocationId)?.length}
                      capacity={space.LocationCapacity ?? 0}
                      negativeScale={negativeScale.value}
                      index={index}
                      area={space}
                      parentArea={selectedArea}
                      areas={spaces}
                      movingItem={space.moving}
                      setMovingItem={() => {
                        focusedLocationId.value = null;
                        setMovingLocationId(space.LocationId);
                        resetScale();
                        setMovingItem(true);
                      }}
                      movingLocationId={movingLocationId}
                      editArea={() => {
                        const spacesCopy = Object.assign([], spaces);
                        const spaceToEdit = spacesCopy.splice(index, 1);
                        setDrawPoints(spaceToEdit[0]?.points ?? []);
                        setDrawMode(true);
                        setShapeName(spaceToEdit[0]?.name ?? '');
                        setLocationSubtype(spaceToEdit[0]?.LocationSubtype);
                        setLocationCapacity(spaceToEdit[0]?.LocationCapacity);
                        setLocationExternalId(spaceToEdit[0]?.ExternalId);
                        setHighPriority(spaceToEdit[0]?.highPriority ?? false);
                        setEditLocationType('space');
                        setEditLocationId(spaceToEdit[0]?.LocationId);
                        setEditingShape(true);
                        setAreaComplete(true);
                        setBeingEdited(space);
                      }}
                      setAreas={setSpaces}
                      imageWidth={imageWidth}
                      imageHeight={imageHeight}
                      getMinX={getMinX}
                      getMinY={getMinY}
                      getMaxX={getMaxX}
                      getMaxY={getMaxY}
                      getPointsString={getPointsString}
                      showAlert={showAlert}
                      editMode={editMode}
                      arrowPress={() => {
                        logEvent('Map', 'pin_arrow');
                        setDrawerOpen(true);
                        setSelectedSpace(space);
                        setShowAreaList(false);
                        setLoading(true);
                      }}
                      isSpace={true}
                      spaceSelected={selectedSpace}
                      areaSelected={selectedSpace?.LocationId === space.LocationId}
                      drawMode={drawMode}
                      setPinMenuOpen={setPinMenuOpen}
                      areaUpdated={() => setAreaUpdated(!areaUpdated)}
                      {...{ occupancyShown }}
                      parentFloor={selectedFloor}
                      ticketsSelected={ticketsSelected}
                    />
                  </RNAnimated.View>
                )}
              </Pressable>
            );
          });
        })()}
    </>
    )
  );

  const TicketsView = () => (
    <>
      {tickets.map((ticket, key) => {
        const { asset } = ticket;
        const firstIndex = tickets.findIndex((t) => t.assetId === asset.id);
        if (key !== firstIndex) return null;
        if (!asset) return null;
        const hasGeoX = ticket.asset.customFields.some(
          (field) => field.name === 'GeoX' && Number(field.value) > 0,
        );
        const assetTickets = tickets.filter((t) => t.assetId === asset.id);
        if (!hasGeoX || !ticketsSelected || editMode || drawMode
          || (selectedFloor
            && ticket.floorId !== selectedFloor?.LocationId)
          || !assetTickets?.length
          || (selectedArea
            && asset.locationId !== selectedArea?.LocationId)
          || (selectedSpace
            && asset.locationId !== selectedSpace?.LocationId)) {
          return null;
        }
        const geoY = Number.parseInt(asset.customFields.find(
          (field) => field.name === 'GeoY',
        )?.value, 10);
        const geoX = Number.parseInt(asset.customFields.find(
          (field) => field.name === 'GeoX',
        )?.value, 10);

        return (
          <Pressable
            style={(state) => [
              {
                position: 'absolute',
                zIndex: state.hovered ? 10 : 1,
                top: geoY,
                left: geoX,
              },
            ]}
            key={key}
            testID='map-ticket-icon-on-the-map-btn'
            onHoverIn={() => {
              if (!movingAssetId) focusedAssetId.value = asset.id;
            }}
            onHoverOut={() => {
              if (!movingAssetId) focusedAssetId.value = null;
            }}
          >
            <Animated.View
              key={key}
              style={negativeScaleStyle}>
              <Ticket
                {...{
                  setMovingItem, isPointInShape, slaNames,
                }}
                key={asset.id}
                asset={asset}
                timestamp={Moment()}
                focusedAssetId={focusedAssetId}
                assetTickets={assetTickets}
                deepLinkTicketId={(route.params as any)?.ticketId}
                mapViewWidth={mapViewWidth}
                mapViewHeight={mapViewHeight}
              />
            </Animated.View>
          </Pressable>
        );
      })}</>
  );

  const currentSelectedArea = areas.find((area) => (
    area?.LocationId === selectedArea?.LocationId
  )) ?? selectedArea;

  const currentSelectedSpace = spaces.find((space) => (
    space?.LocationId === selectedSpace?.LocationId
  )) ?? selectedSpace;

  let drawerCollapsedWidth: number = 0;
  if (showNewSidebar) drawerCollapsedWidth = 63;
  const miniLegendLeftOffset: number = (drawerOpen ? 400 : drawerCollapsedWidth) + 12;

  const MainView = () => (
    <View style={showNewSidebar ? styles.newContainer : styles.container}
      onLayout={({ nativeEvent: { layout: { width, height } } }) => {
        viewWidth.value = width;
        viewHeight.value = height;
      }}>
      {(!smartGuardRoute && selectedTab !== 'assets') && <View style={[
        styles.miniLegendAccordionContainer, { left: miniLegendLeftOffset }]}>
        <Pressable
          onPress={() => setShowMiniLegend(!showMiniLegend)}
          style={[
            styles.miniLegendAccordion,
            showMiniLegend && styles.miniLegendAccordionExpanded,
          ]}
        >
          <Text style={styles.miniLegendAccordionText}>{'Legend'}</Text>
          <FontAwesome
            name={showMiniLegend ? 'angle-up' : 'angle-down'}
            size={20}
            color={Colors.light.hereworksBlack600}
          />
        </Pressable>
        {showMiniLegend ? <MapLegend
          isVisible={false}
          hide={() => setLegendShown(false)}
          {...{ slaNames }}
          miniLegend
          miniLegendEmbedded
          setShowMiniLegend={setShowMiniLegend}
          selectedTab={selectedTab}
        /> : null}
      </View>}
      <View style={{ flex: 1, width: '100%', borderRadius: 10 }}>
        {(ticketsSelected && !showNewSidebar) ? <View style={styles.header}>
          <TicketHeader />
        </View> : null}
        {(editMode && !movingItem) && (
          <View style={styles.editButtons} pointerEvents={'box-none'}>
            {(!drawMode && !selectedSpace) && (
              <Animated.View
                style={{
                  borderWidth: 1,
                  borderColor: Colors.light.hereworksBlue500,
                  borderRadius: 48,
                  marginLeft: 24,
                  backgroundColor: '#fff',
                  transform: [{ translateX: drawerInset }],
                }}
              >
                <Pressable
                  style={(state) => [
                    Style.zoomButton,
                    {
                      backgroundColor: Colors.light.hereworksBlue500,
                      margin: 4,
                      width: 40,
                      height: 40,
                      borderRadius: 20,
                      opacity: state.hovered ? 0.5 : 1,
                    },
                  ]}
                  onPress={() => {
                    logEvent('Map', 'pen_icon');
                    setDrawMode(true);
                    setSelectedTab('assets');
                    resetScale();
                  }}
                  testID='map-edit-map-btn'>
                  <PenIcon16 />
                </Pressable>
              </Animated.View>
            )}
            {(drawMode && !selectedSpace) && <DrawTools
              areaComplete={areaComplete}
              editingShape={editingShape}
              drawPoints={drawPoints}
              redoPoints={redoPoints}
              drawerStyle={drawerStyle}
              setCreateModalVisible={setCreateModalVisible}
              logEvent={logEvent}
              setToolDialogVisible={setToolDialogVisible}
              toolDialogVisible={toolDialogVisible}
              setSelectedTool={setSelectedTool}
              setDrawPoints={setDrawPoints}
              setAreaComplete={setAreaComplete}
              setRedoPoints={setRedoPoints}
              setDrawMode={setDrawMode}
              setEditingShape={setEditingShape}
              setAreas={setAreas}
              setSpaces={setSpaces}
              setBeingEdited={setBeingEdited}
              selectedTool={selectedTool}
              areas={areas}
              spaces={spaces}
              beingEdited={beingEdited}
              selectedArea={selectedArea}
            />}
          </View>
        )}
        <Animated.View
          style={[{
            backgroundColor: 'transparent',
            justifyContent: 'center',
            alignItems: 'flex-end',
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 24,
            zIndex: 5,
          }, sensorStyle,
          showNewSidebar && {
            justifyContent: 'flex-end',
            bottom: 80,
          }]}
          pointerEvents={'box-none'}
        >
          {(isAdmin && !smartGuardRoute && (window.location.href.startsWith('http://localhost'))) && <View style={{
            marginBottom: 10, alignItems: 'center', padding: 8, backgroundColor: Colors.light.hereworksBlue50, borderRadius: 30, borderWidth: 2, borderColor: Colors.light.hereworksBlue500,
          }}>
              <Text style={[Style.dropdownText, { fontSize: 11 }]}>Old UI</Text>
              <Switch toggled={!showNewSidebar} toggle={() => setShowNewSidebar(!showNewSidebar)} />
            </View>}
          <View
            style={[{
              borderWidth: 1,
              borderColor: Colors.light.hereworksBlue500,
              borderRadius: 48,
              marginLeft: 24,
              backgroundColor: '#fff',
            }, Style.dropShadow]}
          >
            <Text style={{ marginTop: 8, textAlign: 'center', fontSize: 12 }}>{scaleLabel}%</Text>
            <ZoomInButton />
            <ZoomOutButton />
            <View style={{ borderColor: Colors.light.hereworksBlue100, borderTopWidth: 1 }}>
              <Pressable
                style={(state) => [Style.zoomButton, { opacity: state.hovered ? 0.5 : 1 }]}
                onPress={() => {
                  logEvent('Map', 'share');
                  showAlert(FEATURE_COMING_SOON, false);
                }}
                testID='map-share-btn'>
                <ShareIcon />
              </Pressable>
            </View>
            {showNewSidebar ? <Pressable
              style={(state) => [{
                opacity: state.hovered ? 0.5 : 1,
                marginTop: 0,
              },
              Style.zoomButton,
              ]}
              onPress={() => {
                logEvent('Map', 'legend');
                setLegendShown(true);
              }}
              testID='map-legend-btn'>
              <HelpCircleIcon hideCircle />
            </Pressable> : null}
          </View>
        </Animated.View>
        {showNewSidebar ? <View style={styles.newFloorContainer} pointerEvents={'box-none'}>
          <View style={{ position: 'absolute', top: 0, right: 90 }}>
            {((assetsSelected || sensorsShown)
              && !smartGuardRoute) && <SubLocationToggleSwitch
                subLocationsEnabled={subLocationsEnabled}
                setSubLocationsEnabled={setSubLocationsEnabled}
                assetsSelected={assetsSelected}
              />}
          </View>
          <View
            style={[styles.newFloorSection, Style.dropShadow, editMode && { paddingTop: 10 }]}
            testID='map-floors-label'
          >
            {editMode && (
              <Pressable
                style={(state) => [state.hovered && Style.hovered]}
                onPress={() => {
                  logEvent('Map', 'edit_floors');
                  setFloorPlanModalVisible(true);
                }}
                testID='map-floors-btn'>
                <EditIcon24 />
              </Pressable>
            )}
            {floorData.map((floor, key) => renderFloorButton(floor, key))}
          </View>
        </View> : <View style={styles.floorContainer} pointerEvents={'box-none'}>
          <View
            style={[styles.floorSection, Style.dropShadow]}
            testID='map-floors-label'
          >
            <Text style={styles.buttonText}>{'Floor'}</Text>
            {editMode && (
              <Pressable
                style={(state) => [state.hovered && Style.hovered]}
                onPress={() => {
                  logEvent('Map', 'edit_floors');
                  setFloorPlanModalVisible(true);
                }}
                testID='map-floors-btn'>
                <EditIcon24 />
              </Pressable>
            )}
            {floorData.map((floor, key) => renderFloorButton(floor, key))}
          </View>
        </View>}
        {(smartGuardRoute || movingItem || showNewSidebar) ? null : <Animated.View
          style={[styles.buttonSection, sensorStyle, !ticketsSelected && { top: 26 }]}
        >
          <View style={[Style.barShadow, styles.buttonContainer]}>
            {!editMode && <MapTabButton
              onPress={() => {
                logEvent('Map', 'tickets');
                if (!ticketsSelected) setSelectedTab('tickets');
              }}
              selected={ticketsSelected}
              testID='map-tickets-btn'
              title='TICKETS'
            />}
            <MapTabButton
              onPress={() => {
                logEvent('Map', 'assets');
                if (!assetsSelected) setSelectedTab('assets');
              }}
              selected={assetsSelected}
              testID='map-assets-btn'
              title='ASSETS'
            />
            {happyEnabled && <MapTabButton
              onPress={() => {
                logEvent('Map', 'occupancy');
                setSelectedTab('occupancy');
                setShowAreaList(true);
              }}
              selected={!assetsSelected && !ticketsSelected && occupancySelected}
              testID='map-occupancy-btn'
              title='OCCUPANCY'
            />}
            {happyEnabled && <>
              <SensorTabButton
                onPress={() => {
                  logEvent('Map', 'sensors');
                  setSelectedTab('sensors');
                }}
                selected={sensorsShown}
                testID='map-sensors-btn'
                title='SENSORS & METERS'
              />
            </>}
          </View>
        </Animated.View>}
        <View style={{ flex: 1 }} onLayout={(event) => {
          const { layout } = event.nativeEvent;
          const { width, height } = layout;
          setMapViewWidth(width);
          setMapViewHeight(height);
        }}>
          {selectedFloor?.Svg && (
            <>
              {showNewSidebar ? null : <View style={[
                styles.fillContainer,
                { zIndex: drawerOpen ? 1 : -1, opacity: drawerOpen ? 1 : 0 },
              ]}>
                <View style={styles.drawerContainer}>
                  {!ticketsSelected
                    && <>
                      <View style={styles.tabsContainer}>
                        {!selectedSpace && <Pressable
                          onPress={() => {
                            logEvent('Map', 'locations');
                            setShowAreaList(true);
                          }}
                          style={(state) => [{
                            borderBottomWidth: 4,
                            borderBottomColor: showAreaList ? Colors.light.hereworksBlue500 : '#fff',
                          }, state.hovered && Style.hovered]}
                          testID='map-locations-btn'>
                          <Text style={[
                            styles.bottomButtonStyle,
                            {
                              color: showAreaList ? Colors.light.hereworksBlue500
                                : Colors.light.hereworksBlack500,
                            }]}>
                            LOCATIONS
                          </Text>
                        </Pressable>}
                        {!occupancyShown && <Pressable
                          onPress={() => {
                            logEvent('Map', 'assets_tab');
                            setShowAreaList(false);
                          }}
                          style={(state) => [{
                            borderBottomWidth: 4,
                            borderBottomColor: !showAreaList ? Colors.light.hereworksBlue500 : '#fff',
                          }, state.hovered && Style.hovered]}
                          testID='map-assets-side-menu-btn'>
                          <Text style={[
                            styles.bottomButtonStyle,
                            {
                              color: !showAreaList ? Colors.light.hereworksBlue500
                                : Colors.light.hereworksBlack500,
                            }]}>
                            {assetsSelected ? 'ASSETS' : 'SENSORS & METERS'}
                          </Text>
                        </Pressable>}
                      </View>
                      {(!showAreaList && !occupancyShown) && (
                        <Animatable.View
                          style={{ alignSelf: 'stretch', flex: 1 }}
                          animation="fadeIn"
                          duration={250}>
                          <ScrollView style={{ alignSelf: 'stretch' }}>
                            {assetsSelected
                              && <View testID='map-asset-list'>
                                <View style={styles.searchBox}>
                                  <SearchIcon style={{ marginLeft: 14 }} />
                                  <TextInput
                                    placeholder="Search by name or ID"
                                    placeholderTextColor={Colors.light.hereworksBlack400}
                                    value={searchValue}
                                    style={styles.searchText}
                                    // onFocus={() => setSearchResultsPaneFocused(false)}
                                    onChangeText={setSearchValue}
                                    onKeyPress={(e) => {
                                      // @ts-ignore
                                      if (e.code === 'ArrowDown') setSearchResultsPaneFocused(true);
                                    }}
                                  />
                                </View>
                                {visibleAssets?.length > 0 ? visibleAssets
                                  .sort((prev: AssetInstance, next: AssetInstance) => prev.name
                                    .toLowerCase().localeCompare(next.name.toLowerCase()))
                                  .map((asset, index: number) => renderAssetRow(asset, index))
                                  : <Text style={styles.noAssetsText}>
                                    {'No assets in this location'}
                                  </Text>}
                              </View>}
                            {!assetsSelected && !ticketsSelected && !occupancySelected
                              && (visibleSensors?.length > 0 ? visibleSensors
                                .sort((prev: AssetInstance, next: AssetInstance) => prev.name
                                  .toLowerCase().localeCompare(next.name.toLowerCase()))
                                .map((sensor, index: number) => renderAssetRow(sensor, index, true))
                                : <Text style={styles.noAssetsText}>
                                  {'No sensors in this location'}
                                </Text>)}
                          </ScrollView>
                        </Animatable.View>
                      )}
                      {(showAreaList || occupancyShown) && (
                        <Animatable.View
                          style={{ flex: 1, alignSelf: 'stretch' }}
                          animation="fadeIn"
                          duration={250}>
                          {showAreaList && <View style={styles.searchBox}>
                            <SearchIcon style={{ marginLeft: 14 }} />
                            <TextInput
                              placeholder="Search by name or ID"
                              placeholderTextColor={Colors.light.hereworksBlack400}
                              value={locationSearchValue}
                              style={styles.searchText}
                              onChangeText={setLocationSearchValue}
                            />
                          </View>}
                          <ScrollView style={{ flex: 1, alignSelf: 'stretch' }}>
                            {!selectedArea
                              && displayAreas.filter((area) => {
                                if (!locationSearchValue?.trim().length) return true;
                                const query = locationSearchValue.toLowerCase();
                                const name = area?.name?.toLowerCase() ?? '';
                                const locationId = String(area?.LocationId ?? '').toLowerCase();
                                return name.includes(query) || locationId.includes(query);
                              }).sort((prev, next) => prev?.name.toLowerCase()
                                .localeCompare(next?.name.toLowerCase()))
                                .map((area, key) => area && (
                                  <View key={key} style={[styles.listItemContainer, {
                                    backgroundColor: focusedLocationId.value === area.LocationId
                                      ? Colors.light.hereworksBlue50 : '#fff',
                                  }]}>
                                    <EyeIcon />
                                    <View style={styles.listItemTextContainer}>
                                      <Text style={styles.listItemLocationName} selectable>
                                        {area.name}</Text>
                                      <Text style={styles.listItemLocationType} selectable>
                                        {LocationSubtypeTitles[area.LocationSubtype] ?? 'Area'}
                                      </Text>
                                    </View>
                                    <Pressable
                                      style={(state: PressableState) => [
                                        styles.listItemIconContainer,
                                        {
                                          borderStartWidth:
                                            (area.polygon || editMode) ? 1 : 0,
                                        },
                                        { opacity: state.hovered ? 0.5 : 1 },
                                      ]}
                                      disabled={!area.polygon && !editMode}
                                      onPress={() => {
                                        logEvent('Map', 'area_row');
                                        if (!area.polygon) {
                                          const areasCopy = Object.assign([], areas);
                                          const areaToEdit = areasCopy.splice(key, 1);
                                          setDrawPoints(areaToEdit[0]?.points ?? []);
                                          setDrawMode(true);
                                          setEditLocationType('area');
                                          setEditLocationId(areaToEdit[0]?.LocationId);
                                          setShapeName(areaToEdit[0]?.name ?? '');
                                          setLocationSubtype(areaToEdit[0]?.LocationSubtype);
                                          setLocationCapacity(areaToEdit[0]?.LocationCapacity);
                                          setLocationExternalId(areaToEdit[0]?.ExternalId);
                                          setHighPriority(areaToEdit[0]?.highPriority ?? false);
                                          savedScale.value = 1;
                                          setBeingEdited(area);
                                          setEditingShape(true);
                                          setEditMode(true);
                                        } else {
                                          setSelectedSpace(null);
                                          setSelectedArea(area);
                                        }
                                      }}
                                      testID={(!area.polygon && editMode)
                                        ? 'draw-area-on-the-map-btn'
                                        : 'map-navigate-to-location-btn'}>
                                      {(!area.polygon && editMode)
                                        && <PenIcon16 color={Colors.light.hereworksBlue500} />}
                                      {area.polygon ? <RightArrow24 /> : <></>}
                                    </Pressable>
                                  </View>
                                ))}
                            {selectedArea
                              && <>
                                {occupancyShown
                                  && <OccupancyWidgets
                                    name={(currentSelectedSpace ?? currentSelectedArea)?.name}
                                    locationType={(currentSelectedSpace
                                      ?? currentSelectedArea)?.LocationSubtype}
                                    goBack={() => {
                                      if (selectedSpace) setSelectedSpace(null);
                                      else setSelectedArea(null);
                                    }}
                                    buildingId={selectedBuildingId}
                                    floorId={selectedFloor?.LocationId}
                                    areaId={selectedArea?.LocationId}
                                    capacity={currentSelectedArea?.LocationCapacity ?? 0}
                                    occupancyPercentage={currentSelectedArea
                                      ?.occupancyPercentage ?? 0}
                                    spaceId={selectedSpace?.LocationId}
                                    externalId={(currentSelectedSpace
                                      ?? currentSelectedArea ?? selectedFloor
                                      ?? selectedBuilding)?.ExternalId}
                                  />}
                                {!selectedSpace && spaces
                                  .filter((space) => {
                                    if (!locationSearchValue?.trim().length) return true;
                                    const query = locationSearchValue.toLowerCase();
                                    const name = space?.name?.toLowerCase() ?? '';
                                    const locationId = String(space?.LocationId ?? '').toLowerCase();
                                    return name.includes(query) || locationId.includes(query);
                                  }).sort((prev, next) => prev?.name
                                    .toLowerCase().localeCompare(next?.name.toLowerCase()))
                                  .map((space, key) => space && (
                                    <View key={key} style={styles.listItemContainer}>
                                      <EyeIcon />
                                      <View style={styles.listItemTextContainer}>
                                        <Text style={styles.listItemLocationName}>
                                          {space?.name}
                                        </Text>
                                        <Text style={styles.listItemLocationType}>
                                          {LocationSubtypeTitles[space?.LocationSubtype] ?? 'Space'}
                                        </Text>
                                      </View>
                                      <Pressable
                                        disabled={selectedSpace?.LocationId === space.LocationId}
                                        style={(state: PressableState) => [
                                          styles.listItemIconContainer,
                                          {
                                            borderStartWidth:
                                              (space.polygon || editMode || occupancyShown) ? 1 : 0,
                                          },
                                          {
                                            opacity: (state.hovered
                                              || selectedSpace?.LocationId === space.LocationId)
                                              ? 0.5 : 1,
                                          },
                                        ]}
                                        onPress={() => {
                                          logEvent('Map', 'space_row');
                                          setSelectedSpace(space);
                                          setShowAreaList(false);
                                        }}
                                        testID='map-navigate-to-location-btn'
                                      >
                                        <RightArrow24 />
                                      </Pressable>
                                    </View>
                                  ))}
                              </>}
                          </ScrollView>
                        </Animatable.View>
                      )}
                    </>}
                  {ticketsSelected
                    && <><View style={styles.tabsContainer}>
                      <View
                        style={{
                          borderBottomWidth: 4,
                          borderBottomColor: Colors.light.hereworksBlue500,
                        }}>
                        <Text
                          style={[
                            styles.bottomButtonStyle, { color: Colors.light.hereworksBlue500 },
                          ]}
                        >
                          TICKETS
                        </Text>
                      </View>
                    </View>
                      <ScrollView style={{ alignSelf: 'stretch' }} testID='tickets-side-menu'>
                        {tickets.map((ticket, index) => {
                          const hasGeoX = ticket.asset?.customFields.some(
                            (field) => field.name === 'GeoX' && Number(field.value) > 0,
                          );
                          if (!hasGeoX || !ticketsSelected || editMode
                            || (selectedFloor
                              && ticket.floorId !== selectedFloor?.LocationId)) {
                            return null;
                          }
                          return (
                            <TicketRow
                              {...{
                                ticket, showAlert, slaNames, index,
                              }}
                              key={`ticket_row_${ticket.id}`}
                              clientId={clientName}
                            />
                          );
                        })}
                      </ScrollView>
                    </>}
                </View>
              </View>}
              <Animated.View
                style={[drawerStyle, { backgroundColor: '#fff' }]}>
                <View
                  style={{ backgroundColor: '#fff' }}>
                  <Animated.View style={[transformStyle, {
                    borderWidth: 8,
                    width: imageWidth + 24,
                    borderColor: editMode ? '#000' : '#fff',
                  }]}>
                    <Animated.View
                      // @ts-ignore
                      style={{
                        transformOrigin: 'top left',
                        width: imageWidth,
                        transform: [{ scale }],
                        cursor: drawMode ? 'crosshair' : 'auto',
                      }}>
                      <GestureDetector gesture={tapGesture}>
                        <RNAnimated.View
                          style={{ zIndex: -1 }}
                          testID='map-asset-list'
                        >
                          <GestureDetector gesture={panGesture}>
                            <Animated.View>
                              <Animatable.View animation={'fadeIn'} duration={200}>
                                <ImageBackground
                                  // @ts-ignore
                                  style={{
                                    width: imageWidth,
                                    height: imageHeight,
                                    cursor: drawMode ? 'crosshair' : 'auto',
                                  }}
                                  resizeMode={'cover'}
                                  source={{ uri: selectedFloor?.Svg }}
                                  onLayout={(event) => {
                                    const { layout } = event.nativeEvent;
                                    const { left, top } = layout as any;
                                    setXStart(left);
                                    setYStart(top);
                                    setXEnd(left + layout.width);
                                    setYEnd(top + layout.height);
                                  }}
                                  testID='map-image'
                                >
                                  {!selectedSpace && <DrawLines />}
                                  {(!selectedSpace && selectedTool === 'ellipse') && <DrawEllipse drawPoints={drawPoints} />}
                                </ImageBackground>
                              </Animatable.View>
                              <AreasAndSpaces />
                              {ticketsSelected && areasAndSpacesPins()}
                              {TicketsView()}
                            </Animated.View>
                          </GestureDetector>
                          {!ticketsSelected && areasAndSpacesPins()}
                          {sensorClusters.map((cluster, key) => {
                            if (assetsSelected || ticketsSelected
                              || (occupancySelected && !smartGuardRoute)) return null;
                            const visibleCluster = (cluster ?? []).filter((sensor) => (
                              !shouldHideSubLocationItems || !isItemInSubLocation(sensor)
                            ));
                            if (visibleCluster.length === 0) return null;

                            const selectedAreaId = String(selectedArea?.LocationId ?? '');
                            const selectedSpaceId = String(selectedSpace?.LocationId ?? '');
                            const selectionFilteredCluster = visibleCluster.filter((sensor) => {
                              if (!selectedArea && !selectedSpace) return true;
                              if (movingAssetId && sensor.id === movingAssetId) return true;
                              const sensorLocationId = String(
                                sensor.locationId ?? sensor.LocationId ?? '',
                              );
                              const sensorSpaceId = String(sensor.spaceId ?? sensor.SpaceId ?? '');
                              const sensorAreaId = String(getAreaForItem(sensor)?.LocationId ?? '');
                              if (selectedSpace) {
                                return sensorLocationId === selectedSpaceId
                                  || sensorSpaceId === selectedSpaceId;
                              }
                              return sensorLocationId === selectedAreaId
                                || sensorAreaId === selectedAreaId;
                            });

                            if (selectionFilteredCluster.length === 0) return null;

                            if (selectionFilteredCluster.length === 1) {
                              const sensor = selectionFilteredCluster[0];
                              const sensorLocationId = String(sensor.locationId ?? sensor.LocationId ?? '');
                              const sensorSpaceId = String(sensor.spaceId ?? sensor.SpaceId ?? '');
                              const sensorAreaId = String(getAreaForItem(sensor)?.LocationId ?? '');
                              const movingThisSensor = movingAssetId === sensor.id;
                              if (!sensor.hasXY || drawMode
                                || ((selectedArea && !selectedSpace && !movingThisSensor)
                                  && sensorLocationId !== selectedAreaId
                                  && sensorAreaId !== selectedAreaId)
                                || ((selectedSpace && !movingThisSensor)
                                  && sensorLocationId !== selectedSpaceId
                                  && sensorSpaceId !== selectedSpaceId)) {
                                return null;
                              }
                              if (selectedFilterCount) {
                                let enabled = false;
                                (sensor.labels ?? []).forEach((label) => {
                                  if (enabled) return;
                                  enabled = sensorsFilter[label];
                                });
                                if (!enabled) return null;
                              }
                              return <SensorPinWrapper
                                key={sensor.id}
                                sensorId={sensor.id}
                                locationId={sensor.locationId}
                                menuOpenId={sensorMenuOpen}
                                movingAssetId={movingAssetId}
                                focusedAssetId={focusedAssetId}
                                focusedLocationId={focusedLocationId}
                              ><Sensor
                                  sensor={sensor}
                                  movingAssetId={movingAssetId}
                                  setMovingItem={setMovingItem}
                                  isPointInShape={isPointInShape}
                                  showAlert={showAlert}
                                  negativeScale={getNegativeScale()}
                                  scale={scale.value}
                                  mapViewWidth={mapViewWidth}
                                  mapViewHeight={mapViewHeight}
                                  sensorHovered={focusedAssetId.value === sensor.id}
                                  focusedAssetId={focusedAssetId}
                                  editMode={editMode}
                                  setSensorMenuOpen={setSensorMenuOpen}
                                  sensorMenuOpen={sensorMenuOpen}
                                  editSensorPosition={() => {
                                    setMovingItem(true);
                                    setDrawerOpen(false);
                                    setDrawMode(false);
                                    sensor.hasXY = true;
                                    const sensorArea = getAreaForItem(sensor);
                                    if (sensorArea) {
                                      setSelectedSpace(null);
                                      setSelectedArea(sensorArea);
                                    }
                                    const initialPlacement = getInitialPlacementForItem(sensor);
                                    sensor.GeoY = initialPlacement.y;
                                    sensor.GeoX = initialPlacement.x;
                                    centerViewOnCoordinate({
                                      x: initialPlacement.x,
                                      y: initialPlacement.y,
                                    }, true);
                                    setMovingAssetId(sensor.id);
                                    sensor.locationFloor = selectedFloor?.Name;
                                  }}
                                  deleteSensorPosition={() => {
                                    const newArr = Object
                                      .assign([], sensors);
                                    newArr.forEach((s) => {
                                      if (sensor.id === s.id) {
                                        delete s.GeoX;
                                        delete s.GeoY;
                                        s.hasXY = false;
                                        s.customFields = s.customFields.filter(
                                          (field) => field.name !== 'GeoX' && field.name !== 'GeoY',
                                        );
                                      }
                                    });
                                    setSensors(newArr);
                                  }}
                                  floorId={selectedFloor?.LocationId}
                                /></SensorPinWrapper>;
                            }
                            const center = clusterCenter(selectionFilteredCluster);
                            return <Pressable key={key}
                              onPress={() => centerViewOnCoordinate({ x: center[0], y: center[1] })}
                              style={(state) => [styles.cluster, {
                                left: center[0] - 20,
                                top: center[1] - 20,
                                zIndex: state.hovered ? 5 : 1,
                              }, Style.circleShadow]}>
                              <Text style={styles.clusterText}>
                                {selectionFilteredCluster.length}</Text>
                            </Pressable>;
                          })}
                          {assetClusters.map((cluster, index) => {
                            if (!assetsSelected) return null;

                            const selectionFilteredCluster = (cluster ?? []).filter((asset) => {
                              if (!asset?.hasXY || drawMode) return false;
                              if (movingAssetId && asset.id === movingAssetId) return true;
                              if (selectedArea && !selectedSpace) {
                                return asset.locationId === selectedArea.LocationId;
                              }
                              if (selectedSpace) {
                                return asset.locationId === selectedSpace.LocationId;
                              }
                              return true;
                            });

                            if (selectionFilteredCluster.length === 0) return null;

                            if (selectionFilteredCluster.length === 1) {
                              const asset = selectionFilteredCluster[0];
                              if (!asset.hasXY || !assetsSelected || drawMode
                                || ((selectedArea && !selectedSpace)
                                  && asset.locationId !== selectedArea.LocationId)
                                || (selectedSpace
                                  && asset.locationId !== selectedSpace.LocationId)) {
                                return null;
                              }

                              return <AssetPinWrapper
                                key={asset.id}
                                assetId={asset.id}
                                locationId={asset.locationId}
                                menuOpenId={assetMenuOpen}
                                movingAssetId={movingAssetId}
                                focusedAssetId={focusedAssetId}
                                focusedLocationId={focusedLocationId}
                              >
                                <Asset
                                  {...{
                                    asset,
                                    setMovingItem,
                                    isPointInShape,
                                    editMode,
                                    showAlert,
                                    translateX,
                                    translateY,
                                    xStart,
                                    xEnd,
                                    yStart,
                                    yEnd,
                                  }}
                                  movingAssetId={movingAssetId}
                                  scale={scale.value}
                                  negativeScale={getNegativeScale()}
                                  mapViewWidth={mapViewWidth}
                                  mapViewHeight={mapViewHeight}
                                  focusedAssetId={focusedAssetId}
                                  setAssetMenuOpen={setAssetMenuOpen}
                                  assetMenuOpen={assetMenuOpen}
                                  editAssetPosition={() => {
                                    setMovingItem(true);
                                    setDrawerOpen(false);
                                    setDrawMode(false);
                                    asset.hasXY = true;
                                    asset.GeoY = 120;
                                    asset.GeoX = 20;
                                    setMovingAssetId(asset.id);
                                    asset.locationFloor = selectedFloor?.Name;
                                  }}
                                  deleteAssetPosition={() => {
                                    const newArr = Object
                                      .assign([], assetsSelected ? assets : sensors);
                                    newArr.forEach((a) => {
                                      if (asset.id === a.id) {
                                        delete a.GeoX;
                                        delete a.GeoY;
                                        a.hasXY = false;
                                        a.customFields = a.customFields.filter(
                                          (field) => field.name !== 'GeoX' && field.name !== 'GeoY',
                                        );
                                      }
                                    });
                                    if (assetsSelected) setAssets(newArr);
                                    else setSensors(newArr);
                                  }}
                                /></AssetPinWrapper>;
                            }
                            const center = clusterCenter(selectionFilteredCluster);
                            return <Pressable key={index}
                              onPress={() => centerViewOnCoordinate({ x: center[0], y: center[1] })}
                              style={(state) => [styles.cluster, {
                                left: center[0] - 20,
                                top: center[1] - 20,
                                zIndex: state.hovered ? 5 : 1,
                              }, Style.circleShadow]}>
                              <Text style={styles.clusterText}>
                                {selectionFilteredCluster.length}</Text>
                            </Pressable>;
                          })}
                        </RNAnimated.View>
                      </GestureDetector>
                      {(drawMode && !selectedSpace)
                        && <DrawPoints
                          drawPoints={drawPoints}
                          setDrawPoints={setDrawPoints}
                          imageWidth={imageWidth}
                          imageHeight={imageHeight}
                          selectedArea={selectedArea}
                          showAlert={showAlert}
                          areaComplete={areaComplete}
                          selectedTool={selectedTool}
                          setAreaComplete={setAreaComplete}
                          editingShape={editingShape}
                        />}
                      {(selectedTool === 'rectangle' || selectedTool === 'ellipse') && !selectedSpace
                        && <DrawRectGrab
                          drawPoints={drawPoints}
                          setDrawPoints={setDrawPoints}
                          setRedoPoints={setRedoPoints}
                          selectedTool={selectedTool}
                        />}
                      {(selectedTool === 'rectangle' || selectedTool === 'ellipse') && !selectedSpace
                        && <DrawLineGrabs />}
                    </Animated.View>
                  </Animated.View>
                </View>
              </Animated.View>
              {(smartGuardRoute || movingItem) ? null : <Animated.View
                style={[{ position: 'absolute' }, drawerStyle]}
              >
                {showNewSidebar ? null : <Pressable
                  style={(state) => [
                    {
                      height: 56,
                      width: 24,
                      position: 'absolute',
                      left: 0,
                      top: 24,
                      backgroundColor: state.hovered
                        ? '#003566'
                        : Colors.light.hereworksDarkBlue,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderTopRightRadius: 4,
                      borderBottomRightRadius: 4,
                    },
                  ]}
                  onPress={() => {
                    logEvent('Map', 'drawer');
                    if (movingItem) {
                      showAlert(
                        'Assets are overlapping',
                        true,
                        null,
                        null,
                        'Please finish placing the asset',
                      );
                    } else {
                      setDrawerOpen(!drawerOpen);
                    }
                  }}
                  testID='map-open-close-side-menu-btn'
                >
                  <LeftArrow
                    color="#fff"
                    style={{ transform: [{ rotateZ: drawerOpen ? '0deg' : '180deg' }] }}
                  />
                </Pressable>}
              </Animated.View>}
            </>
          )}
          {!selectedFloor?.Svg && <View style={{
            backgroundColor: '#fff',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center',
          }}>
            {(loadingFloor || loading)
              && <View style={styles.loader}><ActivityIndicator size={'large'} /></View>}
            {!loadingFloor && <>
              <View style={{
                borderWidth: 1,
                borderColor: Colors.light.hereworksBlue100,
                borderRadius: 16,
                padding: 32,
                alignItems: 'center',
              }}>
                <AddIcon60 />
                <Text style={{
                  fontFamily: 'Poppins_500Medium',
                  fontSize: 18,
                  color: Colors.light.hereworksBlack600,
                  marginTop: 24,
                  marginBottom: 8,
                }}>Map is empty</Text>
                <Text style={{
                  fontFamily: 'Roboto_400Regular',
                  fontSize: 16,
                  color: Colors.light.hereworksBlack400,
                }}>
                  Please add floors and their plans to view the content.
                </Text>
                <Pressable style={(state) => [
                  styles.buttonCommon,
                  {
                    borderColor: Colors.light.hereworksLightBlue,
                    backgroundColor: Colors.light.hereworksLightBlue,
                    alignSelf: 'center',
                    marginTop: 24,
                    opacity: state.hovered ? 0.5 : 1,
                  },
                ]}
                  onPress={() => {
                    logEvent('Map', 'upload_from_blank_map');
                    setFloorPlanModalVisible(true);
                  }}>
                  <Text style={{
                    color: '#fff',
                    fontFamily: 'Roboto_400Regular',
                    fontSize: 16,
                    marginVertical: 8,
                  }}>Upload plan</Text>
                </Pressable>
              </View>
            </>}
          </View>}
          {(showNewSidebar && !smartGuardRoute) ? <Pressable
            style={(state) => [
              {
                height: 56,
                width: 56,
                position: 'absolute',
                left: drawerOpen ? 372 : 30,
                zIndex: 99,
                bottom: 40,
                backgroundColor: state.hovered
                  ? '#003566'
                  : Colors.light.hereworksBlue300,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 28,
                flexDirection: 'row',
              },
            ]}
            onPress={() => {
              logEvent('Map', 'drawer');
              if (movingItem) {
                showAlert(
                  'Assets are overlapping',
                  true,
                  null,
                  null,
                  'Please finish placing the asset',
                );
              } else {
                setDrawerOpen(!drawerOpen);
              }
            }}
            testID='map-open-close-side-menu-btn'
          >
            <LeftArrow
              color="#fff"
              style={{ transform: [{ rotateZ: drawerOpen ? '0deg' : '180deg' }] }}
            />
            <LeftArrow
              color="#fff"
              style={{ transform: [{ rotateZ: drawerOpen ? '0deg' : '180deg' }] }}
            />
          </Pressable> : null}
          {(showNewSidebar && !smartGuardRoute) ? <SideBar
            {...{
              drawerOpen,
              isAdmin,
              editMode,
              smartGuardRoute,
              editorButtonPress,
              ticketsSelected,
              assetsSelected,
              occupancyShown,
              sensorsShown,
              selectedTab,
              setSelectedTab,
              showAreaList,
              setShowAreaList,
              areas: displayAreas,
              assets: visibleAssets,
              sensors: visibleSensors,
              searchValue,
              setSearchValue,
              selectedSpace,
              selectedArea,
              selectedFloor,
              setDrawPoints,
              setAreaComplete,
              setDrawMode,
              setEditingShape,
              setBeingEdited,
              setAssets,
              setSensors,
              setSelectedSpace,
              setSelectedArea,
              setSelectedFloor,
              focusedLocationId,
              setEditLocationType,
              setEditLocationId,
              setShapeName,
              setLocationSubtype,
              setLocationCapacity,
              setLocationExternalId,
              setHighPriorityFilter,
              setEditMode,
              focusedAssetId,
              tickets,
              setFilterOpen,
              filterOpen,
              occupancySelected,
              spaces,
              locationSearchValue,
              setLocationSearchValue,
              assetContextIndex,
              setAssetContextIndex,
              setDrawerOpen,
              setMovingItem,
              setMovingAssetId,
              setLoading,
              setHighPriority,
              savedScale,
              anyFilterApplied,
              subLocationsEnabled,
              centerViewOnCoordinate,
            }}
          /> : null}
        </View>
      </View>
      {(!dateOpen && !showNewSidebar) && <Animated.View
        style={[drawerStyle, { alignSelf: 'flex-start' }]}
      >
        <Pressable
          style={(state) => [{ opacity: state.hovered ? 0.5 : 1 }, styles.legendIcon]}
          onPress={() => {
            logEvent('Map', 'legend');
            setLegendShown(true);
          }}
          testID='map-legend-btn'>
          <QuestionIcon />
        </Pressable>
      </Animated.View>}
    </View>
  );

  const SmartGuardList = () => (
    <ScrollViewStyled>
      {floorData.map((floor, key) => (
        <Pressable
          style={({ hovered }: PressableState) => [
            styles.smartGuardListItem, hovered && { borderColor: Colors.light.hereworksBlue500 },
          ]}
          key={key}
          onPress={() => {
            setSmartGuardListView(false);
            setLoading(true);
            setSelectedFloor(floor);
          }}>
          <Text style={styles.smartGuardTitle}>
            {floor.Name}
          </Text>
          <View style={{ flex: 1 }}>
            <Text style={{
              fontFamily: 'Roboto_400Regular',
              color: Colors.light.hereworksBlack400,
              fontSize: 14,
            }}>Status</Text>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 10 }}>
              <View style={[
                styles.pirOuterCircle,
                floor.motionDetected && { backgroundColor: Colors.light.hereworksRed50 },
              ]}>
                <View style={[
                  styles.pirInnerCircle,
                  floor.motionDetected && { backgroundColor: Colors.light.hereworksRed500 },
                ]} />
              </View>
              <Text style={{
                fontFamily: 'Poppins_600SemiBold',
                fontSize: 16,
              }}>{floor.motionDetected ? 'Motion Detected' : 'Clear'}</Text>
              {floor.guardLastChecked ? <Text style={{
                fontFamily: 'Poppins_400Regular',
                fontSize: 14,
                marginLeft: 8,
                color: Colors.light.hereworksBlack400,
              }}>{'Last detected: '}{floor.guardLastChecked}</Text> : null}
            </View>
          </View>
          <View style={{
            width: 200,
            height: 100,
            backgroundColor: floor.motionDetected ? Colors.light.hereworksRed50
              : Colors.light.hereworksGreen50,
            borderTopStartRadius: 100,
            borderTopEndRadius: 100,
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: 40,
            marginRight: 30,
          }}>
            <View style={{
              width: 100,
              height: 50,
              backgroundColor: floor.motionDetected ? Colors.light.hereworksRed200
                : Colors.light.hereworksGreen200,
              borderTopStartRadius: 50,
              borderTopEndRadius: 50,
            }} />
          </View>
          {floor.motionDetected ? <View style={{ position: 'absolute', right: 220 }}>
            <MotionDetectedIcon />
          </View> : <View style={{ position: 'absolute', right: 220 }}>
            <ShieldCheckIcon />
          </View>}
          <RightArrow24 style={{ marginRight: 16 }} />
        </Pressable>
      ))}
    </ScrollViewStyled>
  );

  const renderTicketsFilter = () => <TicketsFilter
    hideDatePicker
    setFilterOpen={() => setFilterOpen(false)}
    {...{
      assets,
      templates,
      saveFilterToggled,
      setSaveFilterToggled,
      slaNames,
      range,
      users,
      userGroups,
      otherPriorityFilter,
      lowPriorityFilter,
      mediumPriorityFilter,
      highPriorityFilter,
      criticalPriorityFilter,
      setOtherPriorityFilter,
      setLowPriorityFilter,
      setMediumPriorityFilter,
      setHighPriorityFilter,
      setCriticalPriorityFilter,
      doneFilter,
      setDoneFilter,
      inProgressFilter,
      setInProgressFilter,
      notStartedFilter,
      setNotStartedFilter,
      slaOverdueFilter,
      setSlaOverdueFilter,
      slaSort: '',
      setSlaSort: () => { },
      setLoading,
      selectedAssetId,
      setSelectedAssetId,
      setSelectedLocation,
      selectedUserGroupId,
      setSelectedUserGroupId,
      selectedAssignee,
      setSelectedAssignee,
      selectedAssigneeName,
      setSelectedAssigneeName,
      selectedLocation,
      setSelectedFloor,
      selectedTemplateId,
      setSelectedTemplateId,
      selectedIssueId,
      setSelectedIssueId,
    }}
    setTickets={() => { }}
    setSelectedTicket={() => { }}
    selectedRange={0}
    onConfirm={() => { }}
    setSelectedRange={() => { }}
    filterShown={filterOpen}
  />;

  return !notEmpty ? <ScreenEmptyState /> : (
    <View
      style={{
        backgroundColor: Colors.light.hereworksBackground,
        flex: 1,
        overflow: 'hidden',
      }}
    >
      {(showNewSidebar && !smartGuardRoute) ? <View style={{
        position: 'absolute',
        top: 0,
        right: 126,
        flexDirection: 'row',
        alignItems: 'center',
        zIndex: occupancyShown ? 2 : -2,
        marginVertical: 24,
      }}>
        <View style={{ flex: 1 }}>
        </View>
        <View style={Style.row}>
          {occupancyShown && <>
            {smartGuardRoute ? <Text
              style={styles.buttonText}>
              Last updated: {Moment(guardLastUpdated).format('LTS')}</Text> : null}
            {lastPoll ? <Text
              style={styles.buttonText}>
              Last updated: {Moment(range.lastPoll).format('LTS')}</Text> : <Text>{lastPoll}</Text>}
            <DateComponent
              onChange={onOccupancyDateConfirm}
              onCompareChange={() => { }}
              onConfirm={() => { }}
              onOpen={setDateOpen}
              startDate={buildingScreenRange.start}
              endDate={buildingScreenRange.end}
              selectedRange={smartGuardRoute ? 7 : buildingScreenRange.selectedRange}
              testID='map-occupancy-date-picker-btn'
              renderLeft
              smartGuard={smartGuardRoute}
              liveEnabled
            />
            <View
              style={[
                (!isAdmin || smartGuardRoute) && { backgroundColor: 'transparent' },
              ]}
            />
          </>}
          {((assetsSelected || sensorsShown)
            && !smartGuardRoute) && <SubLocationToggleSwitch
              subLocationsEnabled={subLocationsEnabled}
              setSubLocationsEnabled={setSubLocationsEnabled}
              assetsSelected={assetsSelected}
            />}
        </View>
      </View> : <View style={{
        flexDirection: 'row', alignItems: 'center', zIndex: occupancyShown ? 2 : -2, marginVertical: 24,
      }}>
        <View style={{ flex: 1 }}>
          {selectedFloor ? <LocationHeaders /> : null}
          {!selectedFloor && <Text style={styles.pageTitle}>Smart Guard Tour</Text>}
        </View>
        <View style={Style.row}>
          {occupancyShown && <>
            {smartGuardRoute ? <Text
              style={styles.buttonText}>
              Last updated: {Moment(guardLastUpdated).format('LTS')}</Text> : null}
            <DateComponent
              onChange={onOccupancyDateConfirm}
              onCompareChange={() => { }}
              onConfirm={() => { }}
              onOpen={setDateOpen}
              startDate={buildingScreenRange.start}
              endDate={buildingScreenRange.end}
              selectedRange={smartGuardRoute ? 7 : buildingScreenRange.selectedRange}
              testID='map-occupancy-date-picker-btn'
              renderLeft
              smartGuard={smartGuardRoute}
              liveEnabled
            />
            <View
              style={[
                styles.datePickerSeparator,
                (!isAdmin || smartGuardRoute) && { backgroundColor: 'transparent' },
              ]}
            />
          </>}
          {((assetsSelected || sensorsShown)
            && !smartGuardRoute) && <SubLocationToggleSwitch
              subLocationsEnabled={subLocationsEnabled}
              setSubLocationsEnabled={setSubLocationsEnabled}
              assetsSelected={assetsSelected}
            />}
          {(isAdmin && !smartGuardRoute) && <Pressable
            style={(state) => [
              styles.buttonCommon,
              {
                borderColor: Colors.light.hereworksLightBlue,
                backgroundColor: editMode
                  ? Colors.light.hereworksDarkBlue
                  : Colors.light.hereworksLightBlue,
                alignSelf: 'center',
                marginRight: 40,
                opacity: state.hovered ? 0.5 : 1,
                zIndex: -1,
              },
            ]}
            onPress={() => editorButtonPress()}
            testID='map-editor-btn'
          >
            <Text style={styles.editorButton} numberOfLines={1}>
              {editMode ? 'Close editor' : 'Map editor'}
            </Text>
            {editMode ? <DeleteIcon16 color={'#fff'} /> : <EditIcon color={'#fff'} />}
          </Pressable>}
        </View>
      </View>}
      {(smartGuardRoute && smartGuardListView) ? <SmartGuardList /> : MainView()}
      <Modal
        isVisible={filterOpen}
        onBackdropPress={() => {
          logEvent('Map', 'close_tickets_filter');
          setFilterOpen(false);
        }}
        animationIn={'fadeIn'}
        animationOut={'fadeOut'}
        style={Style.modal}
        backdropOpacity={0}
        testID='map-tickets-filter-modal'
      >
        {renderTicketsFilter()}
      </Modal>
      <SetFloorsModal
        isVisible={floorPlanModalVisible}
        hide={() => {
          logEvent('Map', 'close_floors_modal');
          setFloorPlanModalVisible(false);
        }}
        ParentId={selectedBuildingId}
        floors={floorData}
        setFloors={() => setFloorUpdate(!floorUpdate)}
        testID='map-close-floor-edit-modal-btn'
      />
      <AddAreaModal
        shapeIsHighPriority={highPriority}
        {...{
          locationSubtype,
          locationExternalId,
          shapeName,
          locationCapacity,
        }}
        loading={saving}
        editing={editingShape}
        isVisible={createModalVisible}
        isSpace={editLocationType === 'space' || !!selectedArea}
        hide={() => setCreateModalVisible(false)}
        save={(
          name: string,
          isHighPriority: boolean,
          locationType?: string,
          capacity?: number,
          externalId?: string,
        ) => {
          if (locationType && !capacity) {
            showAlert(PROVIDE_CAPACITY);
            return;
          }
          const newAreas = Object.assign([], areas);
          const newSpaces = Object.assign([], spaces);
          const pLabelPoints = [drawPoints.map((p) => [p.x, p.y])];
          const p = polylabel(pLabelPoints);
          const location: any = {
            Name: name,
            Top: getMinY(drawPoints),
            Left: getMinX(drawPoints),
            Right: getMaxX(drawPoints),
            Bottom: getMaxY(drawPoints),
            Points: drawPoints,
            Polygon: getPointsString(drawPoints),
            Priority: isHighPriority === true,
            ParentId: selectedFloor.LocationId,
            ExternalId: externalId,
            LocationCapacity: capacity,
            LocationSubtype: locationType || null,
            pinY: p[1] - 22,
            pinX: p[0] - 18,
          };
          const localLocationInstance: any = {
            name: location.Name,
            top: location.Top,
            bottom: location.Bottom,
            left: location.Left,
            right: location.Right,
            polygon: location.Polygon,
            highPriority: location.IsHighPriority,
            points: location.Points,
            ParentId: location.ParentId,
            assetCount: location.assetCount,
            sensorCount: location.sensorCount,
            meterCount: location.meterCount,
            LocationSubtype: location.LocationSubtype,
            LocationCapacity: location.LocationCapacity,
            pinY: p[1] - 22,
            pinX: p[0] - 18,
          };
          if (beingEdited) location.spaceCount = beingEdited.spaceCount;
          setSaving(true);
          if (editingShape && selectedArea) {
            location.LocationId = editLocationId;
            localLocationInstance.LocationId = editLocationId;
            editSpace({
              LocationId: editLocationId,
              Space: location,
            }).then((response) => {
              if (response.isError) {
                showAlert(response.errorText, true);
                setSaving(false);
              } else {
                const updatedBuildings = structuredClone(buildings);
                updatedBuildings[selectedBuildingId].floors[selectedFloor.LocationId]
                  .areas[selectedArea.LocationId].spaces[editLocationId] = location;
                dispatch(updateLocations(updatedBuildings));
                setSpaces(newSpaces.map((space) => (
                  space.LocationId === editLocationId ? localLocationInstance : space)));
                resetDrawState();
                showAlert(EDIT_SPACE_SUCCESS, false);
              }
            });
          } else if (editingShape) {
            location.LocationId = editLocationId;
            localLocationInstance.LocationId = editLocationId;
            editArea({
              LocationId: editLocationId,
              Area: location,
            }).then((response) => {
              if (!response || response?.isError) {
                showAlert(response.errorText, true);
                setSaving(false);
              } else {
                setAreas(newAreas.map((area) => (
                  area?.LocationId === editLocationId ? localLocationInstance : area)));
                const updatedBuildings = structuredClone(buildings);
                updatedBuildings[selectedBuildingId].floors[selectedFloor.LocationId]
                  .areas[location.LocationId] = location;
                dispatch(updateLocations(updatedBuildings));
                resetDrawState();
                showAlert(EDIT_AREA_SUCCESS, false);
              }
            });
          } else if (selectedArea) {
            addSpace({
              ParentId: selectedArea.LocationId,
              Space: location,
            }).then((response) => {
              if (!response || response?.isError) {
                showAlert(response.errorText, true);
                setSaving(false);
              } else {
                location.LocationId = response.LocationId;
                localLocationInstance.LocationId = response?.LocationId?.LocationId;
                const updatedBuildings = structuredClone(buildings);
                updatedBuildings[selectedBuildingId].floors[selectedFloor.LocationId]
                  .areas[selectedArea.LocationId].spaces[response.LocationId] = location;
                dispatch(updateLocations(updatedBuildings));
                newSpaces.push(localLocationInstance);
                newAreas.forEach((newArea) => {
                  if (newArea?.LocationId === selectedArea?.LocationId) {
                    newArea.space += 1;
                  }
                });
                setSpaces(newSpaces);
                setAreas(newAreas);
                setSelectedSpace(localLocationInstance);
                resetDrawState();
                setAreaUpdated(!areaUpdated);
                showAlert(ADD_SPACE_SUCCESS, false);
              }
            });
          } else {
            addArea({
              ParentId: selectedFloor.LocationId,
              Area: location,
            }).then((response) => {
              if (response.isError) {
                showAlert(response.errorText, true);
                setSaving(false);
              } else {
                location.LocationId = response?.LocationId?.LocationId ?? response?.LocationId;
                localLocationInstance.LocationId = response?.LocationId?.LocationId;
                const updatedBuildings = structuredClone(buildings);
                updatedBuildings[selectedBuildingId].floors[selectedFloor.LocationId]
                  .areas[location.LocationId] = { ...location, spaces: {} };
                dispatch(updateLocations(updatedBuildings));
                newAreas.push(localLocationInstance);
                setAreas(newAreas);
                resetDrawState();
                showAlert(ADD_AREA_SUCCESS, false);
              }
            });
          }
        }}
        cancel={() => {
          const newAreas = Object.assign([], areas);
          const newSpaces = Object.assign([], spaces);
          if (selectedArea && !beingEdited) {
            newSpaces.push(beingEdited);
            setSpaces(newSpaces);
          } else if (!beingEdited) {
            newAreas.push(beingEdited);
            setAreas(newAreas);
          }
          setSaving(false);
        }}
      />
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        setModalVisible={setAlertVisible}
        action={alertAction}
        cancelAction={alertCancelAction}
        actionTitle={alertActionTitle}
        cancelTitle={alertCancelTitle}
        isMessage={!!alertMessage && !alertError}
        message={alertMessage}
        closeTitle={alertMessage ? 'Okay' : 'Close'}
      />
      <MapLegend
        isVisible={legendShown}
        hide={() => setLegendShown(false)}
        {...{ slaNames }}
        setShowMiniLegend={setShowMiniLegend}
        selectedTab={selectedTab}
      />
      {(ticketsSelected && showNewSidebar && !smartGuardRoute) ? <Animated.View
        style={styles.ticketDateContainer}
        pointerEvents='box-none'
      >
        <DateComponent
          onChange={onConfirm}
          onCompareChange={() => { }}
          onConfirm={() => { }}
          onOpen={setDateOpen}
          startDate={range.start}
          endDate={range.end}
          selectedRange={range.selectedRange}
          testID='map-date-picker-btn'
          renderLeft
        />
      </Animated.View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#fff',
    marginHorizontal: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    marginBottom: 20,
    flex: 1,
    overflow: 'hidden',
  },
  newContainer: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#fff',
    flex: 1,
    overflow: 'hidden',
  },
  title: {
    fontSize: 20,
    margin: 40,
    fontWeight: 'bold',
    color: Colors.light.hereworksBlack600,
  },
  buttonText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
    paddingHorizontal: 10,
  },
  buttonCommon: {
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  bottomButtonStyle: {
    fontFamily: 'Poppins_600SemiBold',
    color: '#fff',
    paddingVertical: 16,
    paddingHorizontal: 24,
    textAlign: 'center',
    flex: 1,
  },
  listItemContainer: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    paddingStart: 10,
    marginHorizontal: 24,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    height: 44,
  },
  listItemTextContainer: {
    flex: 1,
    marginStart: 8,
  },
  listItemLocationName: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    lineHeight: 18,
  },
  listItemLocationType: {
    fontSize: 10,
    lineHeight: 12,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginTop: 2,
  },
  listItemIconContainer: {
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderStartColor: Colors.light.hereworksBlue100,
    height: 44,
    width: 44,
  },
  legendIcon: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    backgroundColor: '#FFF',
    borderRadius: 4,
    padding: 6,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 20,
    left: 20,
    zIndex: 5,
    shadowColor: '#323247',
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  miniLegendAccordionContainer: {
    position: 'absolute',
    top: 24,
    zIndex: 10,
    alignItems: 'flex-start',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlack100,
    backgroundColor: Colors.light.plainBackground,
    borderRadius: 8,
    overflow: 'hidden',
    shadowColor: '#323247',
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  miniLegendAccordion: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    minWidth: 210,
  },
  miniLegendAccordionExpanded: {
  },
  miniLegendAccordionText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginEnd: 6,
    flex: 1,
  },
  filterButtonTitle: {
    fontSize: 14,
    lineHeight: 22,
    marginVertical: 5,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginRight: 8,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    backgroundColor: '#fff',
    paddingHorizontal: 24,
    alignSelf: 'flex-end',
  },
  fillContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  tabsContainer: {
    flexDirection: 'row',
    alignSelf: 'stretch',
    marginBottom: 16,
    borderBottomWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    height: 48,
  },
  areaText: {
    paddingLeft: 8,
    fontSize: 18,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  spaceText: {
    paddingLeft: 8,
    fontSize: 18,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlue500,
  },
  locationHead: {
    marginLeft: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  ticketHeaderSub: {
    justifyContent: 'center',
    paddingTop: 24,
    paddingBottom: 16,
    zIndex: 6,
  },
  ticketDateContainer: {
    position: 'absolute',
    top: 24,
    right: 96,
    zIndex: 200,
    elevation: 200,
  },
  editButtons: {
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 24,
    right: 0,
    zIndex: 2,
  },
  searchBox: {
    flex: 1,
    borderRadius: 24,
    paddingVertical: 8,
    backgroundColor: '#fff',
    borderColor: Colors.light.hereworksBlue50,
    borderWidth: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 24,
    marginBottom: 8,
  },
  searchText: {
    flex: 1,
    color: Colors.light.hereworksBlack600,
    marginLeft: 9,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 16.4,
    marginVertical: 8,
    outlineWidth: 0,
  },
  tabButton: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 4,
    borderWidth: 1,
    marginHorizontal: 4,
  },
  editorButton: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    marginVertical: 8,
    marginRight: 8,
  },
  buttonSection: {
    position: 'absolute',
    top: 96,
    right: 24,
    zIndex: 5,
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonContainer: {
    backgroundColor: '#fff',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingVertical: 4,
    borderRadius: 4,
    marginStart: 4,
  },
  datePickerSeparator: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: Colors.light.hereworksBlue100,
    marginHorizontal: 16,
  },
  noAssetsText: {
    flex: 1,
    textAlign: 'center',
    alignSelf: 'center',
    marginTop: '49%',
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  floorSection: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    borderRadius: 48,
    marginLeft: 24,
    marginBottom: 16,
    paddingStart: 6,
    paddingEnd: 10,
    backgroundColor: '#fff',
    flexDirection: 'row',
    alignItems: 'center',
  },
  newFloorSection: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    borderRadius: 48,
    marginBottom: 16,
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  floorContainer: {
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 24,
    zIndex: 5,
  },
  newFloorContainer: {
    backgroundColor: 'transparent',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    position: 'absolute',
    top: 24,
    bottom: 0,
    left: 0,
    right: 24,
    zIndex: 5,
  },
  loader: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  header: {
    borderBottomWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 6,
    height: 80,
    backgroundColor: '#fff',
  },
  filterDot: {
    position: 'absolute',
    top: 5,
    right: 20,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksUrgentRed,
  },
  cluster: {
    position: 'absolute',
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smartGuardListItem: {
    marginHorizontal: 40,
    marginVertical: 6,
    flexDirection: 'row',
    height: 86,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    alignItems: 'center',
  },
  smartGuardTitle: {
    flex: 1,
    fontSize: 20,
    fontFamily: 'Poppins_600SemiBold',
    marginLeft: 24,
  },
  pirOuterCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 8,
    backgroundColor: Colors.light.hereworksGreen50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pirInnerCircle: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksGreen400,
  },
  smartGuardDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    position: 'absolute',
    top: -1,
    right: -1,
  },
  pageTitle: {
    fontSize: 32,
    lineHeight: 48,
    marginLeft: 40,
    marginTop: 24,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  sensorFilter: {
    position: 'absolute',
    width: 274,
    backgroundColor: '#fff',
    top: 54,
    right: 0,
    overflow: 'hidden',
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
  },
  clusterText: {
    fontSize: 14,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  expandPoint: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0,
  },
  drawerContainer: {
    backgroundColor: '#fff',
    borderRightWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    width: 400,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    left: 0,
    height: '100%',
    flex: 1,
    overflow: 'hidden',
  },
});
